// Inbox route: resizable 3-pane layout — accounts sidebar | thread list | reading pane.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  AtSign,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  Forward,
  Eye,
  History,
  Inbox as InboxIcon,
  Search,
  Loader2,
  LogIn,
  Mail,
  MailOpen,
  Ban,
  Menu,
  Minus,
  MoreHorizontal,
  MessageSquare,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Reply as ReplyIcon,
  ReplyAll,
  RotateCcw,
  Send,
  Share2,
  ShieldAlert,
  ShieldOff,
  Sparkles,
  Star,
  Copy,
  Tag as TagIcon,
  Trash,
  Trash2,
  MailQuestion,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useMe, AuthError, goToLogin } from '@/lib/auth';
import { startConnectGmail, useAccounts, type GmailAccount } from '@/lib/accounts';
import {
  useArchiveThread,
  useBlockDomain,
  useMarkDone,
  useMarkUnread,
  useReopenThread,
  useSnoozeThread,
  useSpamThread,
  useThread,
  useThreads,
  useToggleStar,
  useTrashThread,
  useUnarchiveThread,
  useUnsnoozeThread,
  useUnspamThread,
  useUntrashThread,
  type Folder,
  type ThreadListItem,
  type ThreadMessage,
} from '@/lib/threads';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useAddComment,
  useApplyTag,
  useAssign,
  useCreateTag,
  useDeleteComment,
  useRemoveTag,
  useSaveDraft,
  useCancelOutgoing,
  useOutgoing,
  useOutgoingDetail,
  useRescheduleOutgoing,
  useSendReply,
  useTags,
  useThreadActivity,
  useThreadComments,
  useThreadDraft,
  useThreadPresence,
  useWorkspaceUsers,
  type ActivityEntry,
  type PendingSend,
  type ThreadChangedInfo,
  type ThreadComment,
  type ThreadDraft,
  type WorkspaceUser,
} from '@/lib/collab';
import { TAG_COLORS, tagClasses, tagDotClass } from '@/lib/tag-colors';
import { SharedInboxDialog } from '@/components/SharedInboxDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ComposeDialog,
  COMPOSE_DRAFT_KEY,
  pruneStaleComposeDrafts,
  type ComposeInitialValues,
} from '@/components/ComposeDialog';
import { SendSplitButton, formatSendAt, scheduleOptions, toDatetimeLocal } from '@/components/SendSplitButton';
import { confirmPreSend, preSendWarnings } from '@/lib/presend';
import { NotificationPrompt } from '@/components/NotificationPrompt';
import { ShareAccountDialog } from '@/components/ShareAccountDialog';
import { MuteRuleDialog } from '@/components/MuteRuleDialog';
import { TemplatePicker } from '@/components/TemplatePicker';
import { AskMailPanel } from '@/components/AskMailPanel';
import { ComposeDraftsList } from '@/components/ComposeDraftsList';
import { signaturesForAccount, useSignatures } from '@/lib/signatures';
import {
  useAttachmentUploads,
  useFileDrop,
  formatBytes,
  type PendingUpload,
} from '@/lib/attachments';
import { SignatureInlinePreview } from '@/components/SignatureInlinePreview';
import { RecipientChips } from '@/components/RecipientChips';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/RichTextEditor';
import { MenuPicker } from '@/components/ui/menu-picker';
import { Switch } from '@/components/ui/switch';
import { enablePush, disablePush, currentSubscription, readPushState } from '@/lib/push-client';
import { Bell, BellOff } from 'lucide-react';

export const Route = createFileRoute('/inbox')({
  // ?thread=N deep-links (push notifications, research-report citations).
  validateSearch: (s: Record<string, unknown>): { thread?: number } => ({
    thread: s.thread != null && Number.isFinite(Number(s.thread)) ? Number(s.thread) : undefined,
  }),
  component: Inbox,
});

/** True at the md breakpoint and up. Used to mount EITHER the desktop or the
 *  mobile layout — never both. Rendering both (CSS-toggled) mounts two
 *  ReadingPane instances, whose body-portaled ReplyDocks then collide on
 *  desktop (the hidden mobile pane's reply dock leaks in as a phantom second
 *  composer). One layout at a time keeps a single source of truth. */
function useIsDesktop(): boolean {
  const query = '(min-width: 768px)';
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/** One app-level reply window. Persisted as {threadId, mode}; restored
 *  sessions come back minimized and self-close if their draft is empty. */
interface ReplySession {
  threadId: number;
  mode: 'reply' | 'replyAll';
  minimized: boolean;
  /** Undo-send restore payload, consumed by the composer on mount. */
  restoreUploads?: PendingUpload[];
  restored?: boolean;
}
const REPLY_SESSIONS_KEY = 'wharf:reply-sessions:v1';
const COMPOSE_SESSIONS_KEY = 'wharf:compose-sessions:v1';

function Inbox() {
  const { data: me, isLoading: meLoading, error: meError } = useMe();
  const { data: accountsData, isLoading: accountsLoading } = useAccounts();
  const [accountId, setAccountId] = useState<number | null>(null);
  const [threadId, setThreadId] = useState<number | null>(null);
  // ?thread=N deep-link (push-notification taps, research-report citations) —
  // open that thread on arrival. Previously the param was silently ignored.
  const { thread: threadParam } = Route.useSearch();
  useEffect(() => {
    if (threadParam != null) setThreadId(threadParam);
  }, [threadParam]);
  const [folder, setFolder] = useState<Folder>('inbox');

  // Search: q is what's typed; qDebounced is what we actually query with.
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const isDesktop = useIsDesktop();

  // Compose / forward state — MULTIPLE windows on desktop (Gmail-style, docked
  // side-by-side, coexisting with the reply dock). A closed session stays
  // mounted for 60s so the send toast's Undo can reopen it with everything
  // intact, then gets pruned.
  interface ComposeSession {
    key: number;
    initial?: ComposeInitialValues;
    /** The server compose draft this window is editing (opened from Drafts,
     *  restored after a reload, or acquired by autosave). */
    draftId?: number;
    /** Draft id the window was OPENED with — keeps its localStorage key
     *  stable even after autosave assigns a server id. */
    openedDraftId?: number;
    open: boolean;
    /** Restored after a reload: comes back minimized. */
    restored?: boolean;
  }
  // Compose windows with a server draft survive a reload (minimized), like
  // reply windows do. Only the draft id is persisted; content is server-side.
  const [composeSessions, setComposeSessions] = useState<ComposeSession[]>(() => {
    try {
      const ids = JSON.parse(localStorage.getItem(COMPOSE_SESSIONS_KEY) ?? '[]') as unknown[];
      return ids
        .filter((n) => Number.isFinite(Number(n)))
        .slice(0, 3)
        .map((n, i) => ({
          key: i + 1,
          draftId: Number(n),
          openedDraftId: Number(n),
          open: true,
          restored: true,
        }));
    } catch {
      return [];
    }
  });
  const composeSeq = useRef(0);
  if (composeSeq.current === 0) composeSeq.current = composeSessions.length + 1;
  useEffect(() => {
    try {
      localStorage.setItem(
        COMPOSE_SESSIONS_KEY,
        JSON.stringify(composeSessions.filter((c) => c.open && c.draftId).map((c) => c.draftId)),
      );
    } catch {
      /* private mode */
    }
  }, [composeSessions]);
  const composeOpen = composeSessions.some((s) => s.open);
  // Reply windows live HERE, not inside the thread view: a reply survives
  // navigating to Sent or another thread as a minimized dock, and a refresh
  // brings it back minimized. Draft text + attachments are server-side, so
  // only {threadId, mode} is persisted.
  const [replySessions, setReplySessions] = useState<ReplySession[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(REPLY_SESSIONS_KEY) ?? '[]') as {
        threadId?: unknown;
        mode?: unknown;
      }[];
      return raw
        .filter((r) => Number.isFinite(Number(r.threadId)))
        .slice(0, 3)
        .map((r) => ({
          threadId: Number(r.threadId),
          mode: r.mode === 'replyAll' ? ('replyAll' as const) : ('reply' as const),
          minimized: true,
          restored: true,
        }));
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        REPLY_SESSIONS_KEY,
        JSON.stringify(replySessions.map((r) => ({ threadId: r.threadId, mode: r.mode }))),
      );
    } catch {
      /* private mode — sessions just won't survive a refresh */
    }
  }, [replySessions]);
  function openReply(tid: number, mode: 'reply' | 'replyAll', restoreUploads?: PendingUpload[]) {
    setReplySessions((prev) => {
      if (prev.some((r) => r.threadId === tid)) {
        return prev.map((r) =>
          r.threadId === tid ? { ...r, mode, minimized: false, restoreUploads, restored: false } : r,
        );
      }
      if (prev.length >= 3) {
        toast.info('Up to 3 reply windows at a time — close one first');
        return prev;
      }
      return [...prev, { threadId: tid, mode, minimized: false, restoreUploads }];
    });
  }
  function setReplyMinimized(tid: number, minimized: boolean) {
    setReplySessions((prev) => prev.map((r) => (r.threadId === tid ? { ...r, minimized } : r)));
  }
  function closeReply(tid: number) {
    setReplySessions((prev) => prev.filter((r) => r.threadId !== tid));
  }
  // Navigating away from a reply's thread minimizes it; coming back expands it.
  useEffect(() => {
    setReplySessions((prev) =>
      prev.map((r) => ({ ...r, minimized: r.threadId !== threadId })),
    );
  }, [threadId]);
  const replyDockWidthRem = isDesktop
    ? replySessions.reduce((acc, r) => acc + (r.minimized ? 22.75 : 40.75), 0)
    : 0;
  useEffect(() => {
    pruneStaleComposeDrafts();
  }, []);

  function openCompose(initial?: ComposeInitialValues) {
    setComposeSessions((prev) => {
      const openCount = prev.filter((s) => s.open).length;
      if (!isDesktop && openCount >= 1) {
        toast.info('Finish or discard the current message first');
        return prev;
      }
      if (openCount >= 3) {
        toast.info('Up to 3 message windows at a time');
        return prev;
      }
      return [...prev, { key: composeSeq.current++, initial, open: true }];
    });
  }

  function openComposeDraft(draftId: number) {
    setComposeSessions((prev) => {
      // Already open in a window? Don't open a duplicate editor.
      const existing = prev.find((s) => s.open && s.draftId === draftId);
      if (existing) return prev;
      const openCount = prev.filter((s) => s.open).length;
      if (!isDesktop && openCount >= 1) {
        toast.info('Finish or discard the current message first');
        return prev;
      }
      if (openCount >= 3) {
        toast.info('Up to 3 message windows at a time');
        return prev;
      }
      return [...prev, { key: composeSeq.current++, draftId, openedDraftId: draftId, open: true }];
    });
  }

  function setComposeSessionOpen(key: number, open: boolean) {
    setComposeSessions((prev) => prev.map((s) => (s.key === key ? { ...s, open } : s)));
    if (!open) {
      // Prune after the undo window has safely passed (unless it reopened).
      setTimeout(() => {
        setComposeSessions((prev) => prev.filter((s) => s.key !== key || s.open));
      }, 60_000);
    }
  }

  // "Ask your mail" AI search panel. askThreadId pre-scopes to a thread when
  // launched from the reading pane.
  const [askOpen, setAskOpen] = useState(false);
  const [askThreadId, setAskThreadId] = useState<number | null>(null);

  // Ref to the search input so the `/` shortcut can focus it.
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Wrap a state mutation in document.startViewTransition so the mobile
  // list ↔ reading-pane swap crossfades instead of cutting. Falls back to
  // a plain call when the API or display mode doesn't support it.
  function transitionTo(fn: () => void) {
    interface DocVT { startViewTransition?: (cb: () => void) => unknown }
    const d = document as unknown as DocVT;
    if (typeof d.startViewTransition === 'function') {
      d.startViewTransition(() => flushSync(fn));
    } else {
      fn();
    }
  }

  // ----- Bulk selection ---------------------------------------------------
  // Set of thread ids the user has multi-selected. Empty when off.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  // Anchor row for shift-click range selection.
  const lastSelectedIdRef = useRef<number | null>(null);

  function toggleSelect(id: number, opts?: { range?: boolean }) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (opts?.range && lastSelectedIdRef.current != null) {
        const a = threadList.findIndex((t) => t.id === lastSelectedIdRef.current);
        const b = threadList.findIndex((t) => t.id === id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(threadList[i]!.id);
          lastSelectedIdRef.current = id;
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastSelectedIdRef.current = id;
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) =>
      prev.size === threadList.length && threadList.length > 0
        ? new Set()
        : new Set(threadList.map((t) => t.id)),
    );
  }

  function clearSelection() {
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  }

  // Clear selection when folder / account / search changes — those navigate
  // away from the rows the user picked, so the IDs would no longer be visible.
  useEffect(() => {
    clearSelection();
  }, [folder, accountId, qDebounced]);

  // Mobile-only: open/close the slide-in sidebar drawer.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Auto-close the mobile sidebar whenever the user picks something inside it
  // (account, folder, compose). Desktop ignores it.
  function closeMobileSidebar() { setMobileSidebarOpen(false); }

  // Centralised thread fetch so keyboard shortcuts can navigate the list
  // without prop-drilling. ThreadList just consumes the same query handle.
  const threadsQuery = useThreads(accountId, folder, qDebounced);
  const threadList = useMemo(
    () => threadsQuery.data?.pages.flatMap((p) => p.threads) ?? [],
    [threadsQuery.data],
  );

  // Pending sends ('Sending…' rows) shown at the top of the Sent folder so a
  // just-sent email is visible instantly, through the whole undo window.
  const qc = useQueryClient();
  const outgoingQ = useOutgoing();
  const pendingSends = useMemo(() => {
    if (folder !== 'sent') return [];
    const all = outgoingQ.data?.outgoing ?? [];
    return accountId == null ? all : all.filter((o) => o.accountId === accountId);
  }, [folder, outgoingQ.data, accountId]);
  // When a pending send disappears (it actually left), refresh the thread list
  // so the real Sent row replaces the 'Sending…' placeholder without a gap.
  const prevOutgoingIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const ids = new Set((outgoingQ.data?.outgoing ?? []).map((o) => o.id));
    let completed = false;
    for (const id of prevOutgoingIds.current) if (!ids.has(id)) completed = true;
    prevOutgoingIds.current = ids;
    if (completed) {
      // The send just left; its Sent thread is created by the post-send sync a
      // beat later. Refetch now and again shortly after so the real row drops
      // in without a visible gap where the 'Sending…' placeholder used to be.
      void qc.invalidateQueries({ queryKey: ['threads'] });
      const t = setTimeout(() => void qc.invalidateQueries({ queryKey: ['threads'] }), 4000);
      return () => clearTimeout(t);
    }
  }, [outgoingQ.data, qc]);

  const archive = useArchiveThread();
  const unarchive = useUnarchiveThread();
  const trash = useTrashThread();
  const untrash = useUntrashThread();
  const markUnread = useMarkUnread();
  const snooze = useSnoozeThread();
  const unsnooze = useUnsnoozeThread();
  const spam = useSpamThread();
  const unspam = useUnspamThread();
  const blockDomain = useBlockDomain();

  // ----- Undoable single-thread mutations ---------------------------------
  // Wrap the existing mutations so every successful action fires a Sonner
  // toast with an Undo button. The optimistic UI already made the row
  // vanish; clicking Undo restores it via the inverse mutation (which
  // invalidates the threads query, so the row repaints from the server).

  function archiveWithUndo(id: number, onDone?: () => void) {
    archive.mutate(id, {
      onSuccess: () => {
        onDone?.();
        toast.success('Archived', {
          action: { label: 'Undo', onClick: () => unarchive.mutate(id) },
        });
      },
    });
  }

  function trashWithUndo(id: number, onDone?: () => void) {
    trash.mutate(id, {
      onSuccess: () => {
        onDone?.();
        toast.success('Moved to Trash', {
          action: { label: 'Undo', onClick: () => untrash.mutate(id) },
        });
      },
    });
  }

  function snoozeWithUndo(id: number, until: Date, onDone?: () => void) {
    snooze.mutate(
      { id, until: until.toISOString() },
      {
        onSuccess: () => {
          onDone?.();
          toast.success(`Snoozed until ${formatSnoozeUntil(until.toISOString())}`, {
            action: { label: 'Undo', onClick: () => unsnooze.mutate(id) },
          });
        },
      },
    );
  }

  // ----- Bulk action runner -----------------------------------------------
  // Fire one mutation per selected id in parallel, wait for all of them, then
  // clear selection and refresh the list. Errors are swallowed per-row so a
  // single 502 doesn't strand the whole batch.
  const [bulkRunning, setBulkRunning] = useState<null | 'archive' | 'trash' | 'read'>(null);
  async function runBulk(action: 'archive' | 'trash' | 'read'): Promise<void> {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || bulkRunning) return;
    setBulkRunning(action);
    const fetcher = (id: number) => {
      const path =
        action === 'archive' ? `/api/threads/${id}/archive`
        : action === 'trash' ? `/api/threads/${id}/trash`
        : `/api/threads/${id}/read`;
      return fetch(path, { method: 'POST', credentials: 'include' });
    };
    const results = await Promise.allSettled(ids.map(fetcher));
    // A 4xx/5xx fulfils allSettled — count real successes, don't lie.
    const okIds = ids.filter((_, i) => {
      const r = results[i];
      return r?.status === 'fulfilled' && r.value.ok;
    });
    const failed = ids.length - okIds.length;
    setBulkRunning(null);
    void threadsQuery.refetch();
    if (failed > 0) {
      // Keep the selection so the user can retry exactly what failed.
      toast.error(
        okIds.length === 0
          ? `Couldn't ${action} — ${failed} request${failed === 1 ? '' : 's'} failed`
          : `${action} partly failed — ${failed} of ${ids.length} didn't go through`,
      );
      if (okIds.length === 0) return;
    } else {
      clearSelection();
    }

    // Undo toast for the two destructive bulk actions. Mass mark-read isn't
    // worth undoing — the rows are still there, just bolded one way or other.
    if (action === 'archive' || action === 'trash') {
      const label = action === 'archive' ? 'Archived' : 'Moved to Trash';
      const undoPath = action === 'archive' ? 'unarchive' : 'untrash';
      const ids2 = okIds;
      toast.success(`${label} ${ids2.length} thread${ids2.length === 1 ? '' : 's'}`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            await Promise.allSettled(
              ids2.map((id) =>
                fetch(`/api/threads/${id}/${undoPath}`, { method: 'POST', credentials: 'include' }),
              ),
            );
            void threadsQuery.refetch();
          },
        },
      });
    }
  }
  async function runBulkTag(tagId: number): Promise<void> {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || bulkRunning) return;
    setBulkRunning('read'); // reuse the guard: block double-fire while tagging
    const results = await Promise.allSettled(
      ids.map((id) =>
        fetch(`/api/threads/${id}/tags`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ tagId }),
        }),
      ),
    );
    setBulkRunning(null);
    const failed = results.filter((r) => r.status !== 'fulfilled' || !r.value.ok).length;
    if (failed > 0) {
      toast.error(`Tagging partly failed — ${failed} of ${ids.length} didn't go through`);
    } else {
      clearSelection();
    }
    void threadsQuery.refetch();
  }

  // Keyboard shortcuts: j/k navigate, e archive, u toggle unread, r refresh,
  // Esc close. Ignored when typing into an input.
  useEffect(() => {
    function isTyping(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target)) return;
      const idx = threadId == null ? -1 : threadList.findIndex((t) => t.id === threadId);
      switch (e.key) {
        case 'j': {
          const next = threadList[Math.min(idx + 1, threadList.length - 1)];
          if (next) setThreadId(next.id);
          e.preventDefault();
          break;
        }
        case 'k': {
          const prev = threadList[Math.max(idx - 1, 0)];
          if (prev) setThreadId(prev.id);
          e.preventDefault();
          break;
        }
        case 'e':
          if (threadId != null) {
            archiveWithUndo(threadId, () => setThreadId(null));
            e.preventDefault();
          }
          break;
        case '#':
        case 'Delete':
          if (threadId != null) {
            trashWithUndo(threadId, () => setThreadId(null));
            e.preventDefault();
          }
          break;
        case 'u':
          if (threadId != null) {
            markUnread.mutate(threadId);
            e.preventDefault();
          }
          break;
        case 'r':
          void threadsQuery.refetch();
          e.preventDefault();
          break;
        case 'c':
          openCompose();
          e.preventDefault();
          break;
        case '/':
          searchRef.current?.focus();
          searchRef.current?.select();
          e.preventDefault();
          break;
        case 'x':
          if (threadId != null) {
            toggleSelect(threadId);
            e.preventDefault();
          }
          break;
        case 'Escape':
          if (selectedIds.size > 0) {
            clearSelection();
            e.preventDefault();
          } else if (threadId != null) {
            setThreadId(null);
            e.preventDefault();
          }
          break;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, threadList, archive, trash, markUnread, threadsQuery]);

  // Per-row "which inbox" chips, only meaningful when looking across accounts.
  // Must stay above the early returns below so hook order is stable.
  const accountChips = useMemo(() => {
    const m = new Map<number, AccountChip>();
    for (const a of accountsData?.accounts ?? []) {
      m.set(a.id, {
        label: a.nickname?.trim() || shortInbox(a.emailAddress),
        color: accountChipColor(a),
        email: a.emailAddress,
      });
    }
    return m;
  }, [accountsData]);

  if (meLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
          <span className="text-sm">Loading your inbox…</span>
        </div>
      </div>
    );
  }
  if (meError instanceof AuthError) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
          <img
            src="/icon-512.png"
            alt="Wharf Mail"
            width={56}
            height={56}
            className="mx-auto size-14 rounded-2xl shadow-sm"
          />
          <h1 className="mt-4 text-xl font-semibold tracking-tight">Session expired</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign back in with Clougency to pick up where you left off.
          </p>
          <Button onClick={goToLogin} className="mt-6 w-full gap-2">
            <LogIn className="size-4" strokeWidth={2} />
            Sign in again
          </Button>
        </div>
      </div>
    );
  }
  if (!me) return null;

  const accounts = accountsData?.accounts ?? [];
  const showAccountChips = accountId === null && accounts.length > 1;

  // Shared props so the same children render in both layouts.
  const sidebar = (
    <AccountsSidebar
      accounts={accounts}
      loading={accountsLoading}
      selected={accountId}
      onCompose={() => {
        openCompose();
        closeMobileSidebar();
      }}
      onSelect={(id) => {
        setAccountId(id);
        setThreadId(null);
        closeMobileSidebar();
      }}
      folder={folder}
      onFolderChange={(f) => {
        setFolder(f);
        setThreadId(null);
        closeMobileSidebar();
      }}
    />
  );

  const threadListPane = (
    <ThreadList
      folder={folder}
      query={threadsQuery}
      threads={threadList}
      pendingSends={pendingSends}
      accountChips={showAccountChips ? accountChips : null}
      selected={threadId}
      onSelect={(id) => transitionTo(() => setThreadId(id))}
      searchQ={q}
      onSearchQChange={setQ}
      searchRef={searchRef}
      onOpenSidebar={() => setMobileSidebarOpen(true)}
      selectedIds={selectedIds}
      onToggleSelect={toggleSelect}
      onToggleSelectAll={toggleSelectAll}
      onClearSelection={clearSelection}
      onBulk={runBulk}
      onBulkTag={runBulkTag}
      bulkRunning={bulkRunning}
      onSwipeArchive={(id) => archiveWithUndo(id)}
      onSwipeTrash={(id) => trashWithUndo(id)}
      onAsk={() => setAskOpen(true)}
      onOpenDraft={openComposeDraft}
    />
  );

  const readingPane = (
    <ReadingPane
      threadId={threadId}
      folder={folder}
      composeOpen={composeOpen}
      onOpenReply={openReply}
      replyOpenForThread={replySessions.some((r) => r.threadId === threadId)}
      onArchived={() => transitionTo(() => setThreadId(null))}
      onBack={() => transitionTo(() => setThreadId(null))}
      onForward={openCompose}
      onAsk={() => {
        setAskThreadId(threadId);
        setAskOpen(true);
      }}
      archive={archive}
      unarchive={unarchive}
      trash={trash}
      untrash={untrash}
      markUnread={markUnread}
      snooze={snooze}
      unsnooze={unsnooze}
      onArchiveAction={(id) => archiveWithUndo(id, () => setThreadId(null))}
      onTrashAction={(id) => trashWithUndo(id, () => setThreadId(null))}
      onSnoozeAction={(id, until) => snoozeWithUndo(id, until, () => setThreadId(null))}
      spam={spam}
      unspam={unspam}
      blockDomain={blockDomain}
      onSpamAction={(id) =>
        spam.mutate(id, {
          onSuccess: () => {
            setThreadId(null);
            toast.success('Reported as spam · moved to Trash');
          },
        })
      }
      onBlockDomainAction={(id, domain) =>
        blockDomain.mutate(
          { id, domain },
          {
            onSuccess: (r) => {
              setThreadId(null);
              toast.success(`Blocked ${r.domain} · this email moved to Trash, future mail auto-deleted`);
            },
            onError: (e) => {
              if ((e as Error & { code?: string }).code === 'needs_reauth') {
                toast.error('Reconnect this inbox to enable domain blocking (Settings → Accounts).');
              } else {
                toast.error(e instanceof Error ? e.message : 'Block failed');
              }
            },
          },
        )
      }
    />
  );

  return (
    <div className="flex h-full flex-col border-t">
      {isDesktop &&
        replySessions.map((r, i) => {
          const right =
            1.5 +
            replySessions
              .slice(0, i)
              .reduce((acc, x) => acc + (x.minimized ? 22.75 : 40.75), 0);
          return (
            <FloatingReplyDock
              key={r.threadId}
              session={r}
              slotRightRem={right}
              onMinimizedChange={(m) => setReplyMinimized(r.threadId, m)}
              onClose={() => closeReply(r.threadId)}
              onUndoRestore={(tid, uploads, replyAll) =>
                openReply(tid, replyAll ? 'replyAll' : 'reply', uploads)
              }
            />
          );
        })}
      {composeSessions.map((s) => {
        const openKeys = composeSessions.filter((x) => x.open).map((x) => x.key);
        const idx = Math.max(0, openKeys.indexOf(s.key));
        // Slots grow leftward: [reply docks…][compose][compose][compose]
        const right = 1.5 + replyDockWidthRem + idx * (34 + 0.75);
        return (
          <ComposeDialog
            key={s.key}
            open={s.open}
            onOpenChange={(o) => setComposeSessionOpen(s.key, o)}
            initial={s.initial}
            draftId={s.draftId}
            slotRightRem={right}
            // First window keeps the legacy key so old crash-recovery drafts
            // still restore; extra windows get their own. Server-draft windows
            // are isolated per draft.
            storageKey={
              s.openedDraftId
                ? `${COMPOSE_DRAFT_KEY}:server-${s.openedDraftId}`
                : s.key === 1
                  ? COMPOSE_DRAFT_KEY
                  : `${COMPOSE_DRAFT_KEY}:${s.key}`
            }
            initialMinimized={!!s.restored}
            onDraftIdChange={(id) =>
              setComposeSessions((prev) =>
                prev.map((x) => (x.key === s.key ? { ...x, draftId: id ?? undefined } : x)),
              )
            }
          />
        );
      })}
      <AskMailPanel
        open={askOpen}
        onOpenChange={(o) => {
          setAskOpen(o);
          if (!o) setAskThreadId(null);
        }}
        threadId={askThreadId}
        accountId={accountId}
        accountLabel={
          accounts.find((a) => a.id === accountId)?.emailAddress ?? 'this inbox'
        }
        onOpenThread={(tid) => {
          setThreadId(tid);
          setAskOpen(false);
          setAskThreadId(null);
        }}
      />
      {/* Soft, dismissible nudge to enable notifications — first thing on top
          of the inbox so it's discoverable but not blocking. */}
      <NotificationPrompt />

      {/* Only ONE layout is mounted at a time (see useIsDesktop) so we never
          run two ReadingPane instances — which would collide via their
          body-portaled reply docks. */}
      {isDesktop ? (
        /* --- Desktop layout: 3-pane resizable ------------------------------ */
        <div className="min-h-0 flex-1">
          {/* @ts-expect-error react-resizable-panels' types omit the required runtime `direction` prop */}
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={16}>{sidebar}</ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={28}>{threadListPane}</ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={56}>{readingPane}</ResizablePanel>
          </ResizablePanelGroup>
        </div>
      ) : (
        /* --- Mobile layout: one-pane navigation + slide-in sidebar --------- */
        <div className="min-h-0 flex-1">
          {threadId == null ? threadListPane : readingPane}

          {/* Backdrop */}
          <div
            className={cn(
              'fixed inset-0 z-40 bg-black/50 transition-opacity',
              mobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            onClick={closeMobileSidebar}
            aria-hidden
          />
          {/* Slide-in drawer */}
          <aside
            className={cn(
              'fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r bg-card shadow-xl transition-transform duration-200 safe-pt safe-pb safe-pl',
              mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
            )}
            aria-hidden={!mobileSidebarOpen}
          >
            {sidebar}
          </aside>
        </div>
      )}
    </div>
  );
}

const FOLDERS: { id: Folder; label: string; icon: typeof Archive }[] = [
  { id: 'all', label: 'All Mail', icon: Archive },
  { id: 'inbox', label: 'Inbox', icon: InboxIcon },
  { id: 'assigned', label: 'Assigned to me', icon: UserCheck },
  { id: 'done', label: 'Done', icon: CheckCircle2 },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'drafts', label: 'Drafts', icon: FileText },
  { id: 'starred', label: 'Starred', icon: Star },
  { id: 'snoozed', label: 'Snoozed', icon: Clock },
  { id: 'spam', label: 'Spam', icon: ShieldAlert },
  { id: 'trash', label: 'Trash', icon: Trash },
];

function AccountsSidebar({
  accounts,
  loading,
  selected,
  onSelect,
  folder,
  onFolderChange,
  onCompose,
}: {
  accounts: GmailAccount[];
  loading: boolean;
  selected: number | null;
  onSelect: (id: number | null) => void;
  folder: Folder;
  onFolderChange: (f: Folder) => void;
  onCompose: () => void;
}) {
  const [sharedDialogOpen, setSharedDialogOpen] = useState(false);
  return (
    <div className="flex h-full flex-col bg-card">
      {/* Compose button */}
      <div className="p-2 pb-0">
        <Button
          className="w-full justify-start gap-2"
          size="sm"
          onClick={onCompose}
        >
          <Pencil className="size-3.5" strokeWidth={2} />
          Compose
          <span className="ml-auto text-[10px] opacity-50">c</span>
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-3 pb-2 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Folders
        </div>
        <nav className="px-2">
          {FOLDERS.map((f) => {
            const Icon = f.icon;
            const active = folder === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => onFolderChange(f.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-foreground hover:bg-accent/50',
                )}
              >
                <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <span>{f.label}</span>
              </button>
            );
          })}
        </nav>

        <Separator className="my-3" />

        <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Accounts
        </div>
        <nav className="px-2 pb-2">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
              selected === null
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground hover:bg-accent/50',
            )}
          >
            <Mail className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <span>All accounts</span>
          </button>
          {loading && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">Loading…</div>
          )}
          {accounts.map((a) => (
            <SidebarAccountRow
              key={a.id}
              a={a}
              active={selected === a.id}
              onSelect={() => onSelect(a.id)}
            />
          ))}
        </nav>
      </ScrollArea>

      <Separator />
      <div className="space-y-2 p-2">
        <PushToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2">
              <Plus className="size-3.5" strokeWidth={2} />
              Add inbox
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <DropdownMenuItem onClick={() => startConnectGmail('personal')}>
              <Mail className="size-4" strokeWidth={1.75} />
              Personal — visible only to you
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setTimeout(() => setSharedDialogOpen(true), 0);
              }}
            >
              <Users className="size-4" strokeWidth={1.75} />
              Shared — visible to the team
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <SharedInboxDialog open={sharedDialogOpen} onOpenChange={setSharedDialogOpen} />
    </div>
  );
}

function ThreadList({
  folder,
  query: q,
  threads,
  pendingSends,
  accountChips,
  selected,
  onSelect,
  searchQ,
  onSearchQChange,
  searchRef,
  onOpenSidebar,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  onBulk,
  onBulkTag,
  bulkRunning,
  onSwipeArchive,
  onSwipeTrash,
  onAsk,
  onOpenDraft,
}: {
  folder: Folder;
  query: ReturnType<typeof useThreads>;
  threads: ThreadListItem[];
  /** Queued/sending emails to show as 'Sending…' rows atop the Sent folder. */
  pendingSends: PendingSend[];
  /** Map of accountId → chip, or null to hide chips (single-account view). */
  accountChips: Map<number, AccountChip> | null;
  selected: number | null;
  onSelect: (id: number) => void;
  searchQ: string;
  onSearchQChange: (v: string) => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
  onOpenSidebar: () => void;
  selectedIds: Set<number>;
  onToggleSelect: (id: number, opts?: { range?: boolean }) => void;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onBulk: (action: 'archive' | 'trash' | 'read') => void;
  onBulkTag: (tagId: number) => void;
  bulkRunning: null | 'archive' | 'trash' | 'read';
  onSwipeArchive: (id: number) => void;
  onSwipeTrash: (id: number) => void;
  onAsk: () => void;
  onOpenDraft: (draftId: number) => void;
}) {
  const isSearching = searchQ.trim().length > 0;
  const truncated = q.data?.pages.some((p) => p.searchTruncated) ?? false;
  const hasSelection = selectedIds.size > 0;
  const allSelected = threads.length > 0 && selectedIds.size === threads.length;
  /** Refresh = actually fetch: kick a Gmail sync for every visible account,
   *  re-read now (spinner resolves on this), then again shortly after so
   *  freshly-synced rows appear without another tap. */
  const fetchNewMail = useCallback(async () => {
    try {
      await fetch('/api/sync-now', { method: 'POST', credentials: 'include' });
    } catch {
      /* trigger is best-effort — the refetches below still run */
    }
    await q.refetch();
    setTimeout(() => void q.refetch(), 4000);
    setTimeout(() => void q.refetch(), 9000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.refetch]);
  return (
    <div className="flex h-full flex-col bg-background">
      {hasSelection ? (
        <BulkToolbar
          count={selectedIds.size}
          allSelected={allSelected}
          onToggleAll={onToggleSelectAll}
          onClear={onClearSelection}
          onBulk={onBulk}
          onBulkTag={onBulkTag}
          running={bulkRunning}
          onOpenSidebar={onOpenSidebar}
        />
      ) : (
        <div className="flex items-center gap-2 border-b px-4 py-3">
          {/* Hamburger — mobile only */}
          <Button
            variant="ghost"
            size="icon"
            className="size-8 md:hidden"
            onClick={onOpenSidebar}
            aria-label="Open menu"
          >
            <Menu className="size-4" strokeWidth={2} />
          </Button>
          <div className="flex-1 truncate text-sm font-semibold capitalize">
            {folder === 'all' ? 'All Mail' : folder}
            {threads.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {threads.length}
              </span>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-violet-600 dark:text-violet-400"
                onClick={onAsk}
                aria-label="Ask your mail (AI search)"
              >
                <MailQuestion className="size-3.5" strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ask your mail (AI search)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => void fetchNewMail()}
                disabled={q.isFetching}
              >
                <RefreshCw
                  className={cn('size-3.5', q.isFetching && 'animate-spin')}
                  strokeWidth={2}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fetch new mail</TooltipContent>
          </Tooltip>
        </div>
      )}
      {/* Search input */}
      <SearchBar
        value={searchQ}
        onChange={onSearchQChange}
        inputRef={searchRef}
        truncatedHint={isSearching && truncated}
      />
      <PullToRefresh onRefresh={fetchNewMail}>
      <ScrollArea className="flex-1">
        {/* Saved new-message drafts sit atop the Drafts folder; the thread
            rows below are reply drafts. */}
        {folder === 'drafts' && <ComposeDraftsList onOpen={onOpenDraft} />}
        {q.isLoading && <ThreadRowSkeletons />}
        {q.error && (
          <div className="p-4 text-sm text-destructive">
            {q.error instanceof Error ? q.error.message : 'Failed to load threads'}
          </div>
        )}
        {!q.isLoading && threads.length === 0 && pendingSends.length === 0 && (
          <EmptyState folder={folder} isSearching={isSearching} q={searchQ} />
        )}
        {pendingSends.length > 0 && (
          <ul className="border-b">
            {pendingSends.map((p) => (
              <li key={`pending-${p.id}`}>
                <PendingSendRow send={p} />
              </li>
            ))}
          </ul>
        )}
        <ul>
          {threads.map((t, i) => (
            <li key={t.id}>
              {i > 0 && <Separator />}
              <SwipeRow
                onArchiveLeft={() => onSwipeArchive(t.id)}
                onTrashLeft={() => onSwipeTrash(t.id)}
              >
                <ThreadRow
                  t={t}
                  account={accountChips?.get(t.accountId) ?? null}
                  selected={t.id === selected}
                  checked={selectedIds.has(t.id)}
                  selectionActive={selectedIds.size > 0}
                  onOpen={() => onSelect(t.id)}
                  onToggleSelect={(opts) => onToggleSelect(t.id, opts)}
                />
              </SwipeRow>
            </li>
          ))}
        </ul>
        {q.hasNextPage && (
          <div className="p-3 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage && (
                <Loader2 className="size-3.5 animate-spin" strokeWidth={2} />
              )}
              Load more
            </Button>
          </div>
        )}
      </ScrollArea>
      </PullToRefresh>
    </div>
  );
}

// --- Bulk-action toolbar (replaces the list header when 1+ row is checked) ---

function BulkToolbar({
  count,
  allSelected,
  onToggleAll,
  onClear,
  onBulk,
  onBulkTag,
  running,
}: {
  count: number;
  allSelected: boolean;
  onToggleAll: () => void;
  onClear: () => void;
  onBulk: (action: 'archive' | 'trash' | 'read') => void;
  onBulkTag: (tagId: number) => void;
  running: null | 'archive' | 'trash' | 'read';
}  & { onOpenSidebar: () => void }) {
  const tagsQ = useTags();
  const tags = tagsQ.data?.tags ?? [];
  return (
    <div className="flex items-center gap-1 border-b bg-primary/5 px-3 py-2 md:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="size-8 md:hidden"
        onClick={onClear}
        aria-label="Clear selection"
      >
        <X className="size-4" strokeWidth={2} />
      </Button>
      <span className="ml-1 text-xs font-medium tabular-nums text-foreground">
        {count} selected
      </span>
      <button
        type="button"
        onClick={onToggleAll}
        className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
      >
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>
      <div className="ml-auto flex items-center gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => onBulk('read')}
              disabled={running !== null}
              aria-label="Mark as read"
            >
              {running === 'read' ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <MailOpen className="size-4" strokeWidth={1.75} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Mark read</TooltipContent>
        </Tooltip>
        {/* Tag picker */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8" aria-label="Add tag" disabled={running !== null}>
              <TagIcon className="size-4" strokeWidth={1.75} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {tags.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No tags yet. Create one from an open thread first.
              </div>
            ) : (
              tags.map((tag) => (
                <DropdownMenuItem
                  key={tag.id}
                  className="gap-2"
                  onClick={() => onBulkTag(tag.id)}
                >
                  <span className={cn('size-2.5 rounded-full', tagDotClass(tag.color))} />
                  <span className="truncate">{tag.name}</span>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => onBulk('archive')}
              disabled={running !== null}
              aria-label="Archive"
            >
              {running === 'archive' ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Archive className="size-4" strokeWidth={1.75} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Archive</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-destructive hover:text-destructive"
              onClick={() => onBulk('trash')}
              disabled={running !== null}
              aria-label="Delete"
            >
              {running === 'trash' ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Trash className="size-4" strokeWidth={1.75} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8" onClick={onClear} aria-label="Clear selection">
              <X className="size-4" strokeWidth={2} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear selection (Esc)</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// --- Swipe-to-action wrapper (mobile Gmail / Missive style) -----------------
//
// Touch-only. Swiping the row right past the threshold archives; swiping left
// trashes. Vertical scrolling stays unaffected — we wait for the first ~10 px
// of movement to decide which axis the user committed to. Desktop never fires
// touch events, so the row sits at translateX(0) and the action panels behind
// stay invisible.

function SwipeRow({
  children,
  onArchiveLeft,
  onTrashLeft,
}: {
  children: ReactNode;
  // Functions to invoke once a swipe has fully completed.
  onArchiveLeft: () => void;
  onTrashLeft: () => void;
}) {
  const [dx, setDx] = useState(0);
  // Snap = animating the row off-screen after a successful swipe. While
  // snapping we ignore further touches so the action only fires once.
  const [snap, setSnap] = useState<null | 'archive' | 'trash'>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const axis = useRef<null | 'h' | 'v'>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  const THRESHOLD = 80; // px the user must drag past for the action to fire

  function reset() {
    setDx(0);
    axis.current = null;
  }

  function onTouchStart(e: React.TouchEvent) {
    if (snap || e.touches.length !== 1) return;
    startX.current = e.touches[0]!.clientX;
    startY.current = e.touches[0]!.clientY;
    axis.current = null;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (snap || e.touches.length !== 1) return;
    const ddx = e.touches[0]!.clientX - startX.current;
    const ddy = e.touches[0]!.clientY - startY.current;
    // First 10 px decides axis. Bias slightly toward vertical so normal list
    // scrolling doesn't get hijacked by a finger that isn't perfectly straight.
    if (axis.current === null) {
      if (Math.abs(ddx) > 10 || Math.abs(ddy) > 10) {
        axis.current = Math.abs(ddx) > Math.abs(ddy) * 1.3 ? 'h' : 'v';
      }
    }
    if (axis.current === 'h') setDx(ddx);
  }

  function onTouchEnd() {
    if (snap) return;
    if (axis.current !== 'h') {
      setDx(0);
      return;
    }
    const w = elRef.current?.offsetWidth ?? 400;
    if (dx > THRESHOLD) {
      setSnap('archive');
      setDx(w);
      setTimeout(() => {
        onArchiveLeft();
      }, 220);
    } else if (dx < -THRESHOLD) {
      setSnap('trash');
      setDx(-w);
      setTimeout(() => {
        onTrashLeft();
      }, 220);
    } else {
      setDx(0);
    }
  }

  // Block the trailing onClick that fires after a horizontal swipe — without
  // this, swipe-to-archive would also try to open the thread.
  function onClickCapture(e: React.MouseEvent) {
    if (axis.current === 'h' || snap) {
      e.stopPropagation();
      e.preventDefault();
      axis.current = null;
    }
  }

  const archiveOpacity = Math.min(1, Math.max(0, dx) / THRESHOLD);
  const trashOpacity = Math.min(1, Math.max(0, -dx) / THRESHOLD);

  return (
    <div ref={elRef} className="relative overflow-hidden">
      {/* Archive reveal (swipe right) */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-start gap-2 bg-emerald-600 pl-5 text-white"
        style={{ opacity: archiveOpacity }}
        aria-hidden
      >
        <Archive className="size-5" strokeWidth={2} />
        <span className="text-sm font-medium">Archive</span>
      </div>
      {/* Trash reveal (swipe left) */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-end gap-2 bg-destructive pr-5 text-white"
        style={{ opacity: trashOpacity }}
        aria-hidden
      >
        <span className="text-sm font-medium">Delete</span>
        <Trash className="size-5" strokeWidth={2} />
      </div>
      <div
        style={{
          transform: `translateX(${dx}px)`,
          transition: dx === 0 || snap ? 'transform 0.22s ease-out' : 'none',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={reset}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>
    </div>
  );
}

/** Threshold past which a queued send is a deliberate schedule rather than the
 *  30s undo window. Both share status 'queued', so we split on how far out
 *  send_at sits. */
const SCHEDULE_THRESHOLD_MS = 45_000;

function isScheduledSend(send: Pick<PendingSend, 'status' | 'sendAt'>): boolean {
  return send.status === 'queued' && new Date(send.sendAt).getTime() > Date.now() + SCHEDULE_THRESHOLD_MS;
}

/** A queued/sending email, shown atop the Sent folder so it's visible the
 *  instant the user hits Send. Two looks: an in-flight row (spinner + Undo,
 *  30s window) and a scheduled row (clock + send time, click to manage). */
function PendingSendRow({ send }: { send: PendingSend }) {
  const cancel = useCancelOutgoing();
  const qc = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const recipients = send.toAddresses.map(addrOnly).filter(Boolean);
  const who =
    recipients.length === 0
      ? '(no recipient)'
      : recipients.length === 1
        ? recipients[0]
        : `${recipients[0]} +${recipients.length - 1}`;
  const detail = send.subject || (send.kind === 'reply' ? 'Reply' : '(no subject)');
  const scheduled = isScheduledSend(send);

  // Scheduled: a calm, clickable row showing when it'll go — opens a panel to
  // view the email and reschedule / send now / cancel.
  if (scheduled) {
    return (
      <>
        <button
          type="button"
          onClick={() => setManageOpen(true)}
          className="flex w-full items-center gap-3 border-l-[3px] border-sky-400 bg-sky-50/40 px-4 py-3 text-left transition-colors hover:bg-sky-50 dark:border-sky-500/50 dark:bg-sky-950/20 dark:hover:bg-sky-950/30"
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
            <CalendarClock className="size-4" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">To {who}</div>
            <div className="truncate text-xs text-muted-foreground">{detail}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1 text-xs font-medium text-sky-700 dark:text-sky-300">
            <Clock className="size-3.5" strokeWidth={2} />
            {formatSendAt(send.sendAt)}
          </div>
        </button>
        <ScheduledSendDialog outgoingId={send.id} open={manageOpen} onOpenChange={setManageOpen} />
      </>
    );
  }

  // In flight: queued within the undo window, actively 'sending', or 'sent' but
  // waiting on the local sync (no longer cancellable → no Undo).
  const inFlight = send.status === 'queued' || send.status === 'sending';
  return (
    <div className="flex items-center gap-3 border-l-[3px] border-amber-400 bg-amber-50/40 px-4 py-3 dark:border-amber-500/50 dark:bg-amber-950/20">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <Loader2 className="size-4 animate-spin" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">To {who}</div>
        <div className="truncate text-xs text-muted-foreground">
          {detail} · {inFlight ? 'Sending…' : 'Sent'}
        </div>
      </div>
      {inFlight && send.status === 'queued' && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 shrink-0"
          disabled={cancel.isPending}
          onClick={() =>
            cancel.mutate(send.id, {
              onSuccess: () => {
                void qc.invalidateQueries({ queryKey: ['outgoing'] });
                void qc.invalidateQueries({ queryKey: ['threads'] });
                void qc.invalidateQueries({ queryKey: ['thread-draft'] });
                toast.info('Send cancelled');
              },
              onError: (e) => toast.error(e instanceof Error ? e.message : 'Too late to cancel'),
            })
          }
        >
          Undo
        </Button>
      )}
    </div>
  );
}

/** Manage a scheduled send: preview its content, reschedule, send now, or
 *  cancel. Opened from a scheduled PendingSendRow. */
function ScheduledSendDialog({
  outgoingId,
  open,
  onOpenChange,
}: {
  outgoingId: number;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const detailQ = useOutgoingDetail(open ? outgoingId : null);
  const reschedule = useRescheduleOutgoing();
  const cancel = useCancelOutgoing();
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [custom, setCustom] = useState('');

  // Reset the inline reschedule picker whenever the dialog (re)opens.
  useEffect(() => {
    if (open) setPicking(false);
  }, [open]);

  const d = detailQ.data;
  const opts = scheduleOptions();
  const busy = reschedule.isPending || cancel.isPending;
  const stillQueued = d?.status === 'queued';

  function doReschedule(when: Date) {
    reschedule.mutate(
      { id: outgoingId, scheduledAt: when.toISOString() },
      {
        onSuccess: (r) => {
          void qc.invalidateQueries({ queryKey: ['outgoing'] });
          void qc.invalidateQueries({ queryKey: ['outgoing-detail', outgoingId] });
          setPicking(false);
          toast.success(`Rescheduled for ${formatSendAt(r.sendAt)}`);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not reschedule'),
      },
    );
  }

  function doSendNow() {
    // resolveSendAt clamps to ~now + undo window, so this leaves shortly (and
    // stays briefly undoable) rather than firing the instant we ask.
    reschedule.mutate(
      { id: outgoingId, scheduledAt: new Date().toISOString() },
      {
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: ['outgoing'] });
          onOpenChange(false);
          toast.success('Sending now');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send'),
      },
    );
  }

  function doCancel() {
    cancel.mutate(outgoingId, {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['outgoing'] });
        void qc.invalidateQueries({ queryKey: ['threads'] });
        void qc.invalidateQueries({ queryKey: ['thread-draft'] });
        onOpenChange(false);
        toast.info('Scheduled send cancelled');
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not cancel'),
    });
  }

  const bodyPreview = d ? (d.body?.trim() || stripHtmlTags(d.bodyHtml ?? '')) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Scheduled email</DialogTitle>
        </DialogHeader>

        {detailQ.isLoading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}

        {d && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-md border bg-sky-50/50 px-3 py-2 text-sm font-medium text-sky-800 dark:bg-sky-950/20 dark:text-sky-200">
              <CalendarClock className="size-4 shrink-0" strokeWidth={2} />
              {stillQueued
                ? `Sends ${formatSendAt(d.sendAt)}`
                : `No longer scheduled (status: ${d.status})`}
            </div>

            <div className="space-y-1 text-sm">
              <div className="truncate">
                <span className="text-muted-foreground">To </span>
                {d.toAddresses.map(addrOnly).filter(Boolean).join(', ') || '(no recipient)'}
              </div>
              {d.ccAddresses.length > 0 && (
                <div className="truncate">
                  <span className="text-muted-foreground">Cc </span>
                  {d.ccAddresses.map(addrOnly).filter(Boolean).join(', ')}
                </div>
              )}
              <div className="font-medium">{d.subject || '(no subject)'}</div>
            </div>

            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2 text-sm">
              {bodyPreview || <span className="italic text-muted-foreground">(empty body)</span>}
            </div>

            {picking ? (
              <div className="space-y-2 rounded-md border p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Reschedule to
                </div>
                <div className="flex flex-wrap gap-2">
                  {opts.map((o) => (
                    <Button
                      key={o.label}
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => doReschedule(o.when)}
                    >
                      {o.label}
                      <span className="ml-1 text-[10px] text-muted-foreground">{o.sub}</span>
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    className="flex-1 rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring"
                  />
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      const dt = new Date(custom);
                      if (!Number.isNaN(dt.getTime())) doReschedule(dt);
                    }}
                  >
                    Set
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPicking(false)}>
                  Back
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  disabled={busy || !stillQueued}
                  onClick={doCancel}
                >
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                  Cancel send
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={busy || !stillQueued}
                    onClick={() => {
                      setCustom(toDatetimeLocal(new Date(d.sendAt)));
                      setPicking(true);
                    }}
                  >
                    <Clock className="size-3.5" strokeWidth={1.75} />
                    Reschedule
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={busy || !stillQueued}
                    onClick={doSendNow}
                  >
                    <Send className="size-3.5" strokeWidth={2} />
                    Send now
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThreadRow({
  t,
  account,
  selected,
  checked,
  selectionActive,
  onOpen,
  onToggleSelect,
}: {
  t: ThreadListItem;
  /** Which inbox this thread lives in — shown only in the all-accounts view. */
  account: AccountChip | null;
  selected: boolean;
  checked: boolean;
  /** True when a multi-selection exists (changes tap behaviour on touch). */
  selectionActive: boolean;
  onOpen: () => void;
  onToggleSelect: (opts?: { range?: boolean }) => void;
}) {
  // Native-style selection: Cmd/Ctrl-click toggles a row, Shift-click extends
  // a range, plain click opens. On touch, a long-press starts a selection and
  // — once a selection exists — taps toggle. No checkboxes.
  const longPressed = useRef(false);
  const wasTouch = useRef(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const toggleStar = useToggleStar();

  function clearTimer() {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  function handleClick(e: React.MouseEvent) {
    // A completed long-press already toggled; swallow the trailing click.
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      onToggleSelect();
      return;
    }
    if (e.shiftKey) {
      onToggleSelect({ range: true });
      return;
    }
    // Plain interaction: on touch, if a selection is active, tap toggles
    // (Gmail-mobile edit mode); otherwise open the thread.
    if (wasTouch.current && selectionActive) {
      onToggleSelect();
      return;
    }
    onOpen();
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onMouseDown={() => {
        wasTouch.current = false;
      }}
      onTouchStart={(e) => {
        wasTouch.current = true;
        longPressed.current = false;
        const tch = e.touches[0]!;
        startPos.current = { x: tch.clientX, y: tch.clientY };
        clearTimer();
        pressTimer.current = setTimeout(() => {
          longPressed.current = true;
          onToggleSelect();
          if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8);
        }, 450);
      }}
      onTouchMove={(e) => {
        const tch = e.touches[0]!;
        // Any real movement = scroll/swipe, not a long-press.
        if (
          Math.abs(tch.clientX - startPos.current.x) > 8 ||
          Math.abs(tch.clientY - startPos.current.y) > 8
        ) {
          clearTimer();
        }
      }}
      onTouchEnd={clearTimer}
      onTouchCancel={clearTimer}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onOpen();
        } else if (e.key === ' ') {
          e.preventDefault();
          onToggleSelect();
        }
      }}
      className={cn(
        'group relative flex w-full cursor-pointer items-start gap-3 overflow-hidden px-4 py-3 text-left transition-colors hover:bg-accent/50',
        selected && !checked && 'bg-accent',
        // Selected rows get a left accent bar + tint — clearly selected, no box.
        checked && 'bg-primary/10 shadow-[inset_3px_0_0_0_var(--color-primary)]',
      )}
    >
      {/* Avatar doubles as the selection indicator: a filled check replaces it
          when the row is selected (Gmail / Apple Mail pattern — not a box). */}
      {checked ? (
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
          aria-label="Selected"
        >
          <Check className="size-4" strokeWidth={2.5} />
        </div>
      ) : (
        <SenderAvatar from={t.latestFrom} />
      )}
      <div className="min-w-0 flex-1">
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {t.unread && (
            <span
              aria-label="unread"
              className="inline-block size-2 shrink-0 rounded-full bg-primary"
            />
          )}
          <div
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              t.unread ? 'font-semibold text-foreground' : 'font-medium text-foreground/85',
            )}
          >
            {senderDisplay(t.latestFrom)}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={t.starred ? 'Unstar' : 'Star'}
            aria-pressed={t.starred}
            onClick={(e) => {
              e.stopPropagation();
              toggleStar.mutate({ id: t.id, starred: !t.starred });
            }}
            className={cn(
              'rounded p-0.5 transition hover:text-amber-500 focus-visible:opacity-100',
              t.starred
                ? 'text-amber-500'
                : 'text-muted-foreground opacity-0 group-hover:opacity-100',
            )}
          >
            <Star className={cn('size-3.5', t.starred && 'fill-amber-400')} strokeWidth={2} />
          </button>
          <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
            {formatRelative(t.lastMessageAt)}
          </span>
        </div>
      </div>
      {/* Missive-style row: sender name on top, subject as its own line. */}
      <div
        className={cn(
          'mt-0.5 truncate text-[13px]',
          t.unread ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {t.subject}
      </div>
      <div className="mt-1 line-clamp-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">
        {t.snippet}
      </div>
      {t.trashedBy && (
        <div className="mt-1 truncate text-[10px] text-destructive">
          Trashed by {t.trashedBy.name || t.trashedBy.email}
        </div>
      )}
      {t.snoozedUntil && new Date(t.snoozedUntil).getTime() > Date.now() && (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
          <Clock className="size-2.5" strokeWidth={2.5} />
          Snoozed until {formatSnoozeUntil(t.snoozedUntil)}
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        {t.assignee && (
          <span
            className="inline-flex max-w-[150px] items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold text-primary ring-1 ring-inset ring-primary/20"
            title={`Assigned to ${t.assignee.name || t.assignee.email}`}
          >
            <UserCheck className="size-2.5 shrink-0" strokeWidth={2.5} />
            <span className="truncate">{(t.assignee.name || t.assignee.email).split(/\s+/)[0]}</span>
          </span>
        )}
        {t.status === 'done' && (
          <span
            className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-500/20 dark:text-emerald-300"
            title="Done"
          >
            <CheckCircle2 className="size-2.5 shrink-0" strokeWidth={2.5} />
            Done
          </span>
        )}
        {account && (
          <span
            className={cn(
              'inline-flex max-w-[140px] items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold ring-1 ring-inset',
              tagClasses(account.color),
            )}
            title={`In ${account.email}`}
          >
            <AtSign className="size-2.5 shrink-0" strokeWidth={2.5} />
            <span className="truncate">{account.label}</span>
          </span>
        )}
        {t.tags.map((tg) => (
          <span
            key={tg.id}
            className={cn(
              'inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium ring-1 ring-inset',
              tagClasses(tg.color),
            )}
          >
            {tg.name}
          </span>
        ))}
        {t.messageCount > 1 && (
          <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-normal">
            {t.messageCount} messages
          </Badge>
        )}
        {(t.alsoInAccounts?.length ?? 0) > 0 && (
          <Badge
            variant="outline"
            className="h-4 gap-1 px-1.5 text-[9px] font-normal text-muted-foreground"
            title={`Also received in ${t.alsoInAccounts!.map((a) => a.email).join(', ')}`}
          >
            <Users className="size-2.5" strokeWidth={2} />
            also in {t.alsoInAccounts!.length === 1
              ? shortInbox(t.alsoInAccounts![0]!.email)
              : `${t.alsoInAccounts!.length} inboxes`}
          </Badge>
        )}
      </div>
      </div>
    </div>
  );
}

function ReadingPane({
  threadId,
  folder,
  onArchived,
  onBack,
  onForward,
  onAsk,
  archive,
  unarchive,
  trash,
  untrash,
  markUnread,
  snooze,
  unsnooze,
  onArchiveAction,
  onTrashAction,
  onSnoozeAction,
  unspam,
  onSpamAction,
  onBlockDomainAction,
  composeOpen,
  onOpenReply,
  replyOpenForThread,
}: {
  threadId: number | null;
  folder: Folder;
  onArchived: () => void;
  onBack: () => void;
  onForward: (initial: ComposeInitialValues) => void;
  /** Open the Ask-your-mail panel pre-scoped to this thread. */
  onAsk?: () => void;
  /** Whether any new-message composer is open — guards the auto-open-reply
   *  effect (never steal focus from someone mid-compose). */
  composeOpen: boolean;
  /** Open (or expand) the app-level reply window for a thread. */
  onOpenReply: (threadId: number, mode: 'reply' | 'replyAll') => void;
  /** Whether a reply window exists for the thread being viewed. */
  replyOpenForThread: boolean;
  /** Reports whether the desktop reply dock is open, so compose windows dock
   *  to its left instead of underneath it. */
  archive: ReturnType<typeof useArchiveThread>;
  unarchive: ReturnType<typeof useUnarchiveThread>;
  trash: ReturnType<typeof useTrashThread>;
  untrash: ReturnType<typeof useUntrashThread>;
  markUnread: ReturnType<typeof useMarkUnread>;
  snooze: ReturnType<typeof useSnoozeThread>;
  unsnooze: ReturnType<typeof useUnsnoozeThread>;
  onArchiveAction: (id: number) => void;
  onTrashAction: (id: number) => void;
  onSnoozeAction: (id: number, until: Date) => void;
  spam: ReturnType<typeof useSpamThread>;
  unspam: ReturnType<typeof useUnspamThread>;
  blockDomain: ReturnType<typeof useBlockDomain>;
  onSpamAction: (id: number) => void;
  onBlockDomainAction: (id: number, domain: string) => void;
}) {
  const q = useThread(threadId);
  const { data: rpAccountsData } = useAccounts();
  const markDone = useMarkDone();
  const reopen = useReopenThread();
  const toggleStar = useToggleStar();
  // Hooks for the mobile reply sheet MUST live before any early return,
  // otherwise React sees a different hook count between 'no thread' and
  // 'thread loaded' renders and bails with error #310.
  const [mobileReplyOpen, setMobileReplyOpen] = useState(false);
  const [mobileReplyAll, setMobileReplyAll] = useState(false);
  // Which reply surface exists is decided HERE, not by CSS: the desktop dock
  // and the mobile sheet must never be mounted at the same time (a CSS-hidden
  // dock still autofocuses, steals the caret, and autosaves — the phantom
  // second composer).
  const isDesktop = useIsDesktop();
  // Desktop: which composer is expanded (null = just the action buttons),
  // and which tab is active (controlled so TeamCommentBox can jump to it).
  const [tab, setTab] = useState('messages');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [muteDialogOpen, setMuteDialogOpen] = useState(false);
  const draftQ = useThreadDraft(threadId);
  const autoOpenedFor = useRef<number | null>(null);
  // Undo-send: attachments to hand back to the reopened composer. Set by the
  // undo handler, consumed by the composer on mount, cleared on close/navigate.
  const [restoreUploads, setRestoreUploads] = useState<PendingUpload[] | null>(null);
  // The undo toast's closure outlives renders — compare against the CURRENT
  // thread id, not the one captured when the send happened.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  function handleUndoRestore(tid: number, uploads: PendingUpload[], wasReplyAll: boolean) {
    if (tid !== threadIdRef.current) return; // navigated away — draft is safe server-side
    setRestoreUploads(uploads.length > 0 ? uploads : null);
    // Reopen in the SAME mode the send used — reopening a reply-all as plain
    // reply silently drops every Cc recipient on the resend.
    if (isDesktop) {
      // Desktop undo is handled by the root-level dock's own callback; this
      // path only remains for the mobile sheet.
      onOpenReply(tid, wasReplyAll ? 'replyAll' : 'reply');
    } else {
      setMobileReplyAll(wasReplyAll);
      setMobileReplyOpen(true);
    }
  }
  useEffect(() => {
    setTab('messages');
    setSummaryOpen(false);
    setMobileReplyOpen(false);
    setMobileReplyAll(false);
    setRestoreUploads(null);
  }, [threadId]);
  // Auto-expand the reply composer when a saved draft exists — AT MOST ONCE per
  // thread visit (autoOpenedFor), and ONLY as the first thing that happens.
  // CRITICAL invariants, learned the hard way (phantom composer / dup sends):
  //  - never fire while ANY composer is open (dock, compose, or mobile sheet);
  //  - never fire on mobile at all — the dock is a desktop surface, and a
  //    CSS-hidden dock still autofocuses and autosaves;
  //  - never re-fire after the user closes/sends (onClose marks autoOpenedFor),
  //    even though the draft cache may briefly still hold the sent text.
  useEffect(() => {
    if (threadId == null) return;
    if (!isDesktop) return;
    if (composeOpen) return; // don't auto-open a reply on top of the composer
    if (mobileReplyOpen) return; // the mobile sheet owns the draft right now
    if (replyOpenForThread) return; // a reply window already exists — never disturb it
    if (autoOpenedFor.current === threadId) return;
    if ((draftQ.data?.body ?? '').trim()) {
      autoOpenedFor.current = threadId;
      onOpenReply(threadId, 'reply');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQ.data?.body, threadId, composeOpen, replyOpenForThread, isDesktop, mobileReplyOpen]);
  // Any reply window for this thread (manual or auto) counts as this visit's
  // open — so closing it never lets the effect above resurrect the dock from
  // the (possibly stale) draft cache.
  useEffect(() => {
    if (replyOpenForThread && threadId != null) autoOpenedFor.current = threadId;
  }, [replyOpenForThread, threadId]);

  if (!threadId) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-14 text-center">
        <img
          src="/icon-192.png"
          alt=""
          width={56}
          height={56}
          className="size-14 rounded-2xl opacity-50 shadow-sm"
          aria-hidden
        />
        <p className="mt-4 text-sm font-medium">Pick a thread on the left</p>
        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
          Or press <kbd className="rounded border bg-muted px-1 text-[10px]">c</kbd> to start a new one,{' '}
          <kbd className="rounded border bg-muted px-1 text-[10px]">/</kbd> to search.
        </p>
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (q.error || !q.data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        {q.error instanceof Error ? q.error.message : 'Failed to load thread'}
      </div>
    );
  }

  const { thread, messages } = q.data;
  // Our own address on this thread's inbox — used to seed reply recipients and
  // exclude ourselves from reply-all.
  const selfEmail =
    rpAccountsData?.accounts.find((a) => a.id === thread.accountId)?.emailAddress ?? '';
  const busy =
    archive.isPending ||
    unarchive.isPending ||
    trash.isPending ||
    untrash.isPending ||
    markUnread.isPending;
  // In Inbox we show "Archive"; everywhere else "Move to Inbox" is more useful.
  const showUnarchive = folder !== 'inbox' && folder !== 'trash';
  const isTrashed = !!thread.trashedAt;
  const isSnoozed = !!thread.snoozedUntil && new Date(thread.snoozedUntil).getTime() > Date.now();
  const isSpam = folder === 'spam';
  // The latest sender's domain — what 'Block domain' targets.
  const senderDomain = (() => {
    const last = messages[messages.length - 1];
    const m = (last?.from ?? '').match(/@([^>\s]+)/);
    return (m?.[1] ?? '').toLowerCase();
  })();
  // Friendly default for the "Filter mail like this" From field: the sender's
  // display name if it has one ("Wise <no@wise.com>" → "Wise"), else the domain.
  const senderDefaultFrom = (() => {
    const raw = (messages[messages.length - 1]?.from ?? '').trim();
    const named = raw.match(/^\s*"?([^"<]+?)"?\s*</);
    const name = named?.[1]?.trim();
    return name || senderDomain || raw;
  })();


  function handleForward() {
    // Build a forwarded-message attribution block from the last message.
    const last = messages[messages.length - 1];
    const fwdHeader = last
      ? [
          '---------- Forwarded message ----------',
          `From: ${last.from ?? '(unknown)'}`,
          `Date: ${new Date(last.sentAt).toLocaleString()}`,
          `Subject: ${thread.subject}`,
          last.to.length > 0 ? `To: ${last.to.join(', ')}` : null,
          '',
          last.bodyText ?? stripHtmlTags(last.bodyHtml ?? ''),
        ]
          .filter((l) => l !== null)
          .join('\n')
      : '';
    const fwdSubject = thread.subject.startsWith('Fwd:')
      ? thread.subject
      : `Fwd: ${thread.subject}`;
    onForward({
      accountId: thread.accountId,
      subject: fwdSubject,
      body: fwdHeader,
      // Carry the original files along — a forward without its attachments is
      // how attachments "go missing".
      forwardAttachments: (last?.attachments ?? []).map((a) => ({
        url: `/api/messages/${last!.id}/attachments/${a.index}`,
        filename: a.filename,
        mimeType: a.mimeType,
      })),
    });
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-start gap-2 border-b px-4 py-3 md:gap-3 md:px-6 md:py-4">
        {/* Back arrow — mobile only */}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 md:hidden"
          onClick={onBack}
          aria-label="Back to thread list"
        >
          <ArrowLeft className="size-4" strokeWidth={2} />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold tracking-tight md:text-lg">{thread.subject}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>
              {messages.length} {messages.length === 1 ? 'message' : 'messages'}
            </span>
            {thread.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {thread.tags.map((tg) => (
                  <ThreadTagPill key={tg.id} threadId={thread.id} tag={tg} />
                ))}
              </div>
            )}
          </div>
          {isTrashed && (
            <div className="mt-1 text-xs text-destructive">
              <Trash className="mr-1 inline size-3" strokeWidth={2} />
              Trashed
              {thread.trashedBy && (
                <>
                  {' '}
                  by <span className="font-medium">{thread.trashedBy.name || thread.trashedBy.email}</span>
                </>
              )}
              {thread.trashedAt && (
                <>
                  {' '}
                  on{' '}
                  <span title={new Date(thread.trashedAt).toLocaleString()}>
                    {new Date(thread.trashedAt).toLocaleString()}
                  </span>
                </>
              )}
            </div>
          )}
          {isSnoozed && thread.snoozedUntil && (
            <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              <Clock className="mr-1 inline size-3" strokeWidth={2} />
              Snoozed until{' '}
              <span title={new Date(thread.snoozedUntil).toLocaleString()}>
                {formatSnoozeUntil(thread.snoozedUntil)}
              </span>
            </div>
          )}
        </div>
        <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-1 md:w-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-violet-600 hover:text-violet-700 dark:text-violet-400"
                onClick={() => setSummaryOpen((o) => !o)}
                aria-label="AI summary"
              >
                <Sparkles className="size-4" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>AI summary</TooltipContent>
          </Tooltip>
          {onAsk && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-violet-600 hover:text-violet-700 dark:text-violet-400"
                  onClick={onAsk}
                  aria-label="Ask about this thread"
                >
                  <MailQuestion className="size-4" strokeWidth={1.75} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Ask about this thread</TooltipContent>
            </Tooltip>
          )}
          <TagMenu thread={thread} />
          <AssignmentMenu threadId={thread.id} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                disabled={busy}
                onClick={() => markUnread.mutate(thread.id)}
              >
                <MailOpen className="size-4" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Mark as unread (u)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={handleForward}
              >
                <Forward className="size-4" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Forward</TooltipContent>
          </Tooltip>
          {isSnoozed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-amber-600 dark:text-amber-400"
                  disabled={busy || unsnooze.isPending}
                  onClick={() => unsnooze.mutate(thread.id)}
                >
                  {unsnooze.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <Clock className="size-4" strokeWidth={1.75} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Unsnooze</TooltipContent>
            </Tooltip>
          ) : (
            <SnoozeMenu
              busy={busy || snooze.isPending}
              onPick={(until) => onSnoozeAction(thread.id, until)}
            />
          )}
          {showUnarchive && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={busy}
                  onClick={() => unarchive.mutate(thread.id)}
                >
                  {unarchive.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <ArchiveRestore className="size-4" strokeWidth={1.75} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move to Inbox</TooltipContent>
            </Tooltip>
          )}
          {!isTrashed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={busy}
                  onClick={() => onArchiveAction(thread.id)}
                >
                  {archive.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <Archive className="size-4" strokeWidth={1.75} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive (e)</TooltipContent>
            </Tooltip>
          )}
          {isTrashed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={busy}
                  onClick={() =>
                    untrash.mutate(thread.id, { onSuccess: () => onArchived() })
                  }
                >
                  {untrash.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <RotateCcw className="size-4" strokeWidth={1.75} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Restore from Trash</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  disabled={busy}
                  onClick={() => onTrashAction(thread.id)}
                >
                  {trash.isPending ? (
                    <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <Trash className="size-4" strokeWidth={1.75} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete (#)</TooltipContent>
            </Tooltip>
          )}
          {/* Conversation status — Done / Reopen */}
          {thread.status === 'done' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  disabled={busy || reopen.isPending}
                  onClick={() =>
                    reopen.mutate(thread.id, {
                      onSuccess: () => {
                        onArchived();
                        toast.success('Reopened');
                      },
                    })
                  }
                  aria-label="Reopen"
                >
                  <ArchiveRestore className="size-4" strokeWidth={1.75} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reopen</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                  disabled={busy || markDone.isPending}
                  onClick={() =>
                    markDone.mutate(thread.id, {
                      onSuccess: () => {
                        onArchived();
                        toast.success('Marked done');
                      },
                    })
                  }
                  aria-label="Mark done"
                >
                  <CheckCircle2 className="size-4" strokeWidth={1.75} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark done</TooltipContent>
            </Tooltip>
          )}
          {/* Star toggle (Gmail STARRED) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('size-8', thread.starred && 'text-amber-500 hover:text-amber-600')}
                disabled={busy}
                onClick={() => toggleStar.mutate({ id: thread.id, starred: !thread.starred })}
                aria-label={thread.starred ? 'Unstar' : 'Star'}
                aria-pressed={thread.starred}
              >
                <Star className={cn('size-4', thread.starred && 'fill-amber-400')} strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{thread.starred ? 'Unstar' : 'Star'}</TooltipContent>
          </Tooltip>
          {/* More actions: spam / not-spam / block domain */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label="More actions" disabled={busy}>
                <MoreHorizontal className="size-4" strokeWidth={1.75} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setMuteDialogOpen(true)} className="gap-2">
                <BellOff className="size-4" strokeWidth={1.75} />
                AI filter…
              </DropdownMenuItem>
              {isSpam ? (
                <DropdownMenuItem
                  onClick={() =>
                    unspam.mutate(thread.id, {
                      onSuccess: () => {
                        onArchived();
                        toast.success('Moved back to inbox');
                      },
                    })
                  }
                  className="gap-2"
                >
                  <ShieldOff className="size-4" strokeWidth={1.75} />
                  Not spam
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onSpamAction(thread.id)} className="gap-2">
                  <ShieldAlert className="size-4" strokeWidth={1.75} />
                  Report spam
                </DropdownMenuItem>
              )}
              {senderDomain && (
                <DropdownMenuItem
                  onClick={() => {
                    if (
                      confirm(
                        `Block ${senderDomain}?\n\nThis email moves to Trash now, and a Gmail filter sends all future mail from this domain straight to Trash. You can undo by deleting the filter in Gmail.`,
                      )
                    ) {
                      onBlockDomainAction(thread.id, senderDomain);
                    }
                  }}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <Ban className="size-4" strokeWidth={1.75} />
                  Block {senderDomain}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <CollisionBanner threadId={thread.id} />
      <ThreadProvenance threadId={thread.id} />
      <MuteRuleDialog
        open={muteDialogOpen}
        onOpenChange={setMuteDialogOpen}
        threadId={thread.id}
        messageId={messages[messages.length - 1]?.id ?? null}
        senderDomain={senderDomain || (messages[messages.length - 1]?.from ?? 'this sender')}
        defaultFrom={senderDefaultFrom}
        senderAddress={parseSender(messages[messages.length - 1]?.from).email || undefined}
      />
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mx-6 mt-3 grid w-fit grid-cols-3">
          <TabsTrigger value="messages" className="gap-1.5">
            <Mail className="size-3.5" strokeWidth={1.75} />
            Messages
            <span className="text-[10px] text-muted-foreground">{messages.length}</span>
          </TabsTrigger>
          <TabsTrigger value="comments" className="gap-1.5">
            <MessageSquare className="size-3.5" strokeWidth={1.75} />
            Comments
            <CommentCountBadge threadId={thread.id} />
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5">
            <History className="size-3.5" strokeWidth={1.75} />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="flex-1">
            <ThreadAiDraftCard threadId={thread.id} />
            {summaryOpen && <ThreadSummaryCard threadId={thread.id} />}
            <div className="space-y-4 px-4 py-4 md:px-6">
              {messages.map((m, i) => (
                <MessageCard
                  key={m.id}
                  m={m}
                  defaultCollapsed={i < messages.length - 1}
                />
              ))}
            </div>
          </ScrollArea>

          {/* Desktop: explicit Reply / Reply all / Forward actions. Email
              composing only happens after a deliberate click — the always-
              visible input at the very bottom is for the TEAM, not the
              customer (Missive model). */}
          <div className="hidden md:block">
            <div className="flex items-center gap-2 border-t px-6 py-3">
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  // Manual open counts as this visit's open — the auto-open
                  // effect must never fire again for this thread (e.g. right
                  // after send, when the draft cache is briefly stale).
                  autoOpenedFor.current = thread.id;
                  onOpenReply(thread.id, 'reply');
                }}
              >
                <ReplyIcon className="size-3.5" strokeWidth={2} />
                Reply
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  autoOpenedFor.current = thread.id;
                  onOpenReply(thread.id, 'replyAll');
                }}
              >
                <ReplyAll className="size-3.5" strokeWidth={2} />
                Reply all
              </Button>
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={handleForward}>
                <Forward className="size-3.5" strokeWidth={1.75} />
                Forward
              </Button>
            </div>
            <TeamCommentBox
              threadId={thread.id}
              onViewComments={() => setTab('comments')}
            />
          </div>

          {/* Mobile: sticky action bar that opens a focused reply sheet.
              MOUNT-gated on !isDesktop — exactly one reply surface may exist. */}
          {!isDesktop && (
            <>
              <MobileReplyBar
                onReply={() => setMobileReplyOpen(true)}
                onReplyAll={() => {
                  setMobileReplyAll(true);
                  setMobileReplyOpen(true);
                }}
                onForward={handleForward}
              />
              <MobileReplyDialog
                open={mobileReplyOpen}
                onOpenChange={(o) => {
                  setMobileReplyOpen(o);
                  if (!o) {
                    setMobileReplyAll(false);
                    setRestoreUploads(null);
                    // Same discipline as the desktop dock: closing/sending ends
                    // this visit's reply session; don't let the auto-open
                    // effect act on a stale draft cache.
                    autoOpenedFor.current = thread.id;
                  }
                }}
                threadId={thread.id}
                accountId={thread.accountId}
                defaultReplyAll={mobileReplyAll}
                messages={messages}
                selfEmail={selfEmail}
                initialUploads={restoreUploads ?? undefined}
                onUndoRestore={handleUndoRestore}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="comments" className="flex min-h-0 flex-1 flex-col">
          <CommentsPanel threadId={thread.id} />
        </TabsContent>

        <TabsContent value="activity" className="flex min-h-0 flex-1 flex-col">
          <ActivityFeed threadId={thread.id} />
        </TabsContent>
      </Tabs>
      {/* The desktop reply dock is rendered by the Inbox root (FloatingReplyDock)
          so it survives navigation and refresh; this pane only opens it. */}
    </div>
  );
}

// --- Assignment menu ------------------------------------------------------

function AssignmentMenu({ threadId }: { threadId: number }) {
  const usersQ = useWorkspaceUsers();
  const assign = useAssign(threadId);
  const ws = usersQ.data?.users ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5">
          <UserPlus className="size-3.5" strokeWidth={1.75} />
          Assign
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => assign.mutate(null)}>
          <X className="size-4" strokeWidth={1.75} />
          Unassign
        </DropdownMenuItem>
        {ws.map((u) => (
          <DropdownMenuItem key={u.id} onClick={() => assign.mutate(u.id)}>
            <UserCheck className="size-4" strokeWidth={1.75} />
            <span className="truncate">{u.name || u.email}</span>
          </DropdownMenuItem>
        ))}
        {usersQ.isLoading && (
          <DropdownMenuItem disabled>
            <Loader2 className="size-4 animate-spin" />
            Loading…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// --- Reply composer -------------------------------------------------------

// --- Mobile reply action bar + focused-composer dialog --------------------
//
// On phones the 3-row inline composer at the bottom of the messages tab is
// easy to miss — bg-card/30 melts into the page and the Send icon doesn't
// read as a CTA. Replace it on < md with a clear sticky [Reply] / [Reply
// all] / [Forward] action row; tapping Reply opens a focused composer in a
// Dialog that takes most of the viewport.

function MobileReplyBar({
  onReply,
  onReplyAll,
  onForward,
}: {
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
}) {
  return (
    <div className="border-t bg-card/80 px-3 py-2 backdrop-blur md:hidden">
      <div className="flex items-center gap-2">
        <Button
          onClick={onReply}
          size="lg"
          className="h-11 flex-1 gap-2 text-sm font-medium"
        >
          <Send className="size-4" strokeWidth={2} />
          Reply
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="size-11 shrink-0"
              onClick={onReplyAll}
              aria-label="Reply all"
            >
              <Users className="size-4" strokeWidth={1.75} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Reply all</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="size-11 shrink-0"
              onClick={onForward}
              aria-label="Forward"
            >
              <Forward className="size-4" strokeWidth={1.75} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Forward</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function MobileReplyDialog({
  open,
  onOpenChange,
  threadId,
  accountId,
  defaultReplyAll,
  messages,
  selfEmail,
  initialUploads,
  onUndoRestore,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: number;
  accountId: number;
  defaultReplyAll: boolean;
  messages: ThreadMessage[];
  selfEmail: string;
  initialUploads?: PendingUpload[];
  onUndoRestore?: (threadId: number, uploads: PendingUpload[], replyAll: boolean) => void;
}) {
  const { to, cc } = useMemo(
    () => computeReplyRecipients(messages, selfEmail, defaultReplyAll),
    [messages, selfEmail, defaultReplyAll],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[92dvh] max-h-[92dvh] flex-col gap-0 p-0 sm:max-w-2xl"
        // Drafts autosave, but an accidental outside tap mid-thought is
        // still jarring — require the explicit X / Escape to dismiss.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">{defaultReplyAll ? 'Reply all' : 'Reply'}</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <ReplyComposer
            key={`${threadId}-${defaultReplyAll ? 'all' : 'one'}`}
            threadId={threadId}
            accountId={accountId}
            autoFocus
            onSent={() => onOpenChange(false)}
            // The dialog already gives the composer a frame; ask it to render
            // taller and without the outer border so it fills the sheet.
            inDialog
            initialReplyAll={defaultReplyAll}
            editableRecipients
            initialTo={to}
            initialCc={cc}
            lastSeenMessageId={messages[messages.length - 1]?.id}
            initialUploads={initialUploads}
            onUndoRestore={onUndoRestore}
            onDiscarded={() => onOpenChange(false)}
            knownNames={namesOnThread(messages)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Extract the bare address from a "Name <addr@x>" or "addr@x" header value. */
function addrOnly(s: string | null | undefined): string {
  if (!s) return '';
  const m = s.match(/<([^>]+)>/);
  return (m ? m[1]! : s).trim();
}

/**
 * Seed reply recipients from the thread, the way the server used to derive
 * them: To = the latest message NOT from our own inbox; on reply-all, Cc =
 * everyone else on that message (minus ourselves + dupes).
 */
function computeReplyRecipients(
  messages: ThreadMessage[],
  selfEmail: string,
  replyAll: boolean,
): { to: string[]; cc: string[] } {
  const self = selfEmail.toLowerCase();
  let target: ThreadMessage | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const f = addrOnly(messages[i]!.from).toLowerCase();
    if (f && f !== self) {
      target = messages[i];
      break;
    }
  }
  if (!target) target = messages[messages.length - 1];
  const to = target?.from ? [addrOnly(target.from)].filter(Boolean) : [];
  let cc: string[] = [];
  if (replyAll && target) {
    const seen = new Set<string>([self, ...to.map((t) => t.toLowerCase())]);
    cc = [...(target.to ?? []), ...(target.cc ?? [])].map(addrOnly).filter((a) => {
      const k = a.toLowerCase();
      if (!a || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }
  return { to, cc };
}

/**
 * Docked, compose-style reply panel (Reply / Reply all). Floats bottom-right
 * with no backdrop so the thread stays visible — and unlike the old inline
 * box it exposes editable To/Cc/Bcc. Desktop only (mobile uses the sheet).
 */
/** App-level reply window: fetches its own thread so it can live outside
 *  the reading pane (surviving navigation to Sent, other threads, and a
 *  refresh). Draft text + attachments come from the server draft. */
function FloatingReplyDock({
  session,
  slotRightRem,
  onMinimizedChange,
  onClose,
  onUndoRestore,
}: {
  session: ReplySession;
  slotRightRem: number;
  onMinimizedChange: (m: boolean) => void;
  onClose: () => void;
  onUndoRestore: (threadId: number, uploads: PendingUpload[], replyAll: boolean) => void;
}) {
  const q = useThread(session.threadId);
  const { data: accountsData } = useAccounts();
  const draftQ = useThreadDraft(session.threadId);
  // A session restored after a refresh whose draft turns out to be empty is
  // just noise — drop it once the draft has loaded.
  useEffect(() => {
    if (!session.restored || !draftQ.data) return;
    const empty =
      !(draftQ.data.body ?? '').trim() && (draftQ.data.attachments ?? []).length === 0;
    if (empty) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.restored, draftQ.data]);
  if (!q.data) return null;
  const { thread, messages } = q.data;
  const selfEmail =
    accountsData?.accounts.find((a) => a.id === thread.accountId)?.emailAddress ?? '';
  return (
    <ReplyDock
      thread={{ id: thread.id, accountId: thread.accountId, subject: thread.subject }}
      mode={session.mode}
      messages={messages}
      selfEmail={selfEmail}
      initialUploads={session.restoreUploads}
      onUndoRestore={onUndoRestore}
      onClose={onClose}
      minimized={session.minimized}
      onMinimizedChange={onMinimizedChange}
      slotRightRem={slotRightRem}
    />
  );
}

function ReplyDock({
  thread,
  mode,
  messages,
  selfEmail,
  initialUploads,
  onUndoRestore,
  onClose,
  minimized,
  onMinimizedChange,
  slotRightRem = 1.5,
}: {
  thread: { id: number; accountId: number; subject: string };
  mode: 'reply' | 'replyAll';
  messages: ThreadMessage[];
  selfEmail: string;
  initialUploads?: PendingUpload[];
  onUndoRestore?: (threadId: number, uploads: PendingUpload[], replyAll: boolean) => void;
  onClose: () => void;
  /** Controlled by the Inbox root: navigating away minimizes, coming back
   *  expands, and the state survives a refresh. */
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  /** Distance from the right edge in rem — docks tile leftward. */
  slotRightRem?: number;
}) {
  const { to, cc } = useMemo(
    () => computeReplyRecipients(messages, selfEmail, mode === 'replyAll'),
    [messages, selfEmail, mode],
  );
  return createPortal(
    <div
      role="dialog"
      aria-label={mode === 'replyAll' ? 'Reply all' : 'Reply'}
      style={{ right: `${slotRightRem}rem` }}
      className={cn(
        'fixed z-50 hidden flex-col border bg-card shadow-2xl md:flex',
        'md:inset-x-auto md:bottom-0 md:rounded-t-lg md:border-b-0',
        minimized ? 'h-auto md:w-[22rem]' : 'max-h-[82vh] md:w-[40rem]',
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 rounded-t-lg border-b bg-secondary/70 px-4 py-2',
          minimized && 'cursor-pointer border-b-0',
        )}
        onClick={() => minimized && onMinimizedChange(false)}
      >
        <span className="min-w-0 truncate text-sm font-semibold">
          {mode === 'replyAll' ? 'Reply all' : 'Reply'} · {thread.subject || '(no subject)'}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              onMinimizedChange(!minimized);
            }}
            aria-label={minimized ? 'Expand reply' : 'Minimize reply'}
          >
            <Minus className="size-3.5" strokeWidth={2.5} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close reply"
            title="Close reply (draft is saved)"
          >
            <X className="size-3.5" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
      {/* Keep the composer mounted when minimized (just hidden) — unmounting a
          contentEditable wipes whatever was typed. */}
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', minimized && 'hidden')}>
        <ReplyComposer
          key={`${thread.id}-${mode}`}
          threadId={thread.id}
          accountId={thread.accountId}
          autoFocus
          inDialog
          initialReplyAll={mode === 'replyAll'}
          editableRecipients
          initialTo={to}
          initialCc={cc}
          lastSeenMessageId={messages[messages.length - 1]?.id}
          initialUploads={initialUploads}
          onUndoRestore={onUndoRestore}
          onSent={onClose}
          onDiscarded={onClose}
          knownNames={namesOnThread(messages)}
        />
      </div>
    </div>,
    document.body,
  );
}

function ReplyComposer({
  threadId,
  accountId,
  onSent,
  autoFocus = false,
  inDialog = false,
  initialReplyAll = false,
  editableRecipients = false,
  initialTo,
  initialCc,
  lastSeenMessageId,
  initialUploads,
  onUndoRestore,
  onDiscarded,
  knownNames,
}: {
  threadId: number;
  accountId: number;
  /** Called after a successful send — used by MobileReplyDialog to close. */
  onSent?: () => void;
  /** email → display name for people on the thread; feeds the pre-send
   *  "wrong person in the greeting" check. */
  knownNames?: Record<string, string>;
  /** Focus the textarea on mount (mobile dialog wants this). */
  autoFocus?: boolean;
  /** When inside a Dialog, drop the border-t/background and let the textarea
   *  expand to fill the dialog body. */
  inDialog?: boolean;
  /** Pre-set the reply-all toggle (the mobile bar exposes a separate button
   *  for it). */
  initialReplyAll?: boolean;
  /** Show editable To/Cc/Bcc rows (the docked compose-style reply). When off,
   *  recipients are derived server-side from the thread + reply-all flag. */
  editableRecipients?: boolean;
  /** Initial To/Cc when editableRecipients — computed from the thread. */
  initialTo?: string[];
  initialCc?: string[];
  /** Newest message id visible when the composer opened — the collision
   *  guard's snapshot. The server 409s if the thread moved past it. */
  lastSeenMessageId?: number;
  /** Undo-send restore: re-populate the attachment chips (their server rows
   *  were un-consumed by the cancel). */
  initialUploads?: PendingUpload[];
  /** Called when the user clicks Undo/Cancel on a sent toast — the host pane
   *  reopens the composer with the draft + attachments restored. */
  onUndoRestore?: (threadId: number, uploads: PendingUpload[], replyAll: boolean) => void;
  /** Called after Discard draft wipes everything — host closes the composer. */
  onDiscarded?: () => void;
}) {
  const sigs = useSignatures();
  const { data: accountsData } = useAccounts();
  const allAccounts = accountsData?.accounts ?? [];
  // Send-as: defaults to the thread's own account.
  const [fromAccountId, setFromAccountId] = useState(accountId);
  useEffect(() => setFromAccountId(accountId), [accountId]);
  const accountSigs = signaturesForAccount(sigs.data?.signatures, fromAccountId);
  const hasSig = accountSigs.length > 0;
  // null = the account's default signature.
  const [sigId, setSigId] = useState<number | null>(null);
  useEffect(() => setSigId(null), [fromAccountId]);

  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [replyAll, setReplyAll] = useState(initialReplyAll);
  // Collision-guard snapshot: captured ONCE at composer mount, deliberately not
  // updated as the thread refreshes behind it — a message that arrives while
  // typing should trip the guard even if the UI already fetched it.
  const [lastSeenAtOpen] = useState(lastSeenMessageId);
  // Editable recipients (compose-style reply). Seeded once from the thread.
  const [to, setTo] = useState<string[]>(initialTo ?? []);
  const [cc, setCc] = useState<string[]>(initialCc ?? []);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState((initialCc?.length ?? 0) > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [hydrated, setHydrated] = useState(false);
  const rteRef = useRef<RichTextEditorHandle | null>(null);
  // Latest typed text, mirrored into a ref so the hydration effect can tell
  // whether the user has already started writing — without re-running on
  // every keystroke.
  const bodyTextRef = useRef('');
  useEffect(() => {
    bodyTextRef.current = bodyText;
  }, [bodyText]);
  useEffect(() => {
    if (autoFocus) rteRef.current?.focus();
  }, [autoFocus]);

  const draftQ = useThreadDraft(threadId);
  const saveDraft = useSaveDraft(threadId);
  const send = useSendReply(threadId);
  const cancelOutgoing = useCancelOutgoing();
  const qc = useQueryClient();
  const att = useAttachmentUploads();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Drag-and-drop with a self-expiring veil (can't get stuck — see useFileDrop).
  const replyDrop = useFileDrop((files) => {
    const images = files.filter((f) => /^image\/(png|jpe?g|webp|gif)$/i.test(f.type));
    const rest = files.filter((f) => !/^image\/(png|jpe?g|webp|gif)$/i.test(f.type));
    if (images.length > 0) rteRef.current?.insertImageFiles(images);
    if (rest.length > 0) att.addFiles(rest);
  });
  // Undo-send restore: bring the attachment chips back once on mount (their
  // server rows were un-consumed by the cancel).
  useEffect(() => {
    if (initialUploads?.length) att.restore(initialUploads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate composer body from the server draft once per thread. Drafts
  // store the HTML string (legacy plain-text drafts render fine as HTML).
  useEffect(() => {
    setHydrated(false);
  }, [threadId]);
  // Attachment-change tracking for autosave/flush (null = not hydrated yet).
  const lastSavedAttsRef = useRef<string | null>(null);
  // True once this mount has ever shown text (hydrated or typed). A composer
  // that never had content must not be allowed to autosave emptiness over a
  // real server draft — that is exactly how an undone send got wiped.
  const everHadContentRef = useRef(false);
  useEffect(() => {
    if (!draftQ.data) return;
    const draftBody = (draftQ.data.body ?? '').trim();
    if (hydrated) {
      // Late hydration: the first cache we saw was empty (zeroed on send) and
      // the server's restored draft arrived afterwards. Apply it as long as
      // the user hasn't typed anything into the blank editor.
      if (draftBody && !everHadContentRef.current && bodyTextRef.current.trim() === '') {
        rteRef.current?.setHtml(draftQ.data.body);
        everHadContentRef.current = true;
      }
      return;
    }
    // The draft fetch is async + the editor autofocuses. Only touch the editor
    // when there's an actual saved draft to restore AND the user hasn't started
    // typing — otherwise calling setHtml resets the contentEditable and yanks
    // the caret to the start a couple seconds after they opened the reply.
    if (draftBody && bodyTextRef.current.trim() === '') {
      rteRef.current?.setHtml(draftQ.data.body);
      everHadContentRef.current = true;
    }
    // Restore the saved attachment chips — uploads survive server-side, and
    // before this they silently vanished with the composer.
    const savedAtts = draftQ.data.attachments ?? [];
    if (savedAtts.length > 0 && att.uploads.length === 0) {
      att.restore(
        savedAtts.map((a) => ({
          localId: `draft-${a.id}`,
          filename: a.filename,
          sizeBytes: a.sizeBytes,
          status: 'done' as const,
          uploaded: a,
        })),
      );
    }
    lastSavedAttsRef.current = savedAtts.map((a) => a.id).join(',');
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQ.data, hydrated]);

  // Debounced autosave on body OR attachment change (skip until hydrated so
  // we don't write back an empty state before the draft arrives).
  const attIdsKey = att.readyIds.join(',');
  if (bodyText.trim()) everHadContentRef.current = true;
  useEffect(() => {
    if (!hydrated) return;
    // A blank composer that never showed content has nothing to save — and
    // saving '' would clobber a draft the server just restored (undo-send).
    if (!bodyText.trim() && att.readyIds.length === 0 && !everHadContentRef.current) return;
    const bodyUnchanged = (draftQ.data?.body ?? '') === bodyHtml;
    const attsUnchanged =
      lastSavedAttsRef.current === null || lastSavedAttsRef.current === attIdsKey;
    if (bodyUnchanged && attsUnchanged) return;
    setSaveState('saving');
    const idsAtSave = att.readyIds;
    const t = setTimeout(() => {
      saveDraft.mutate(
        { body: bodyHtml, attachmentIds: idsAtSave },
        {
          onSuccess: () => {
            lastSavedAttsRef.current = idsAtSave.join(',');
            setSaveState('saved');
          },
          onError: () => setSaveState('idle'),
        },
      );
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyHtml, hydrated, attIdsKey]);
  // Flush on unmount: the debounce cleanup above CANCELS a pending save, so
  // closing the dock (whose tooltip promises "draft is saved") within 600ms
  // of the last keystroke would drop it. Direct fetch, not the mutation — a
  // mutation owned by an unmounting component isn't guaranteed to fire.
  const flushRef = useRef({
    body: '',
    text: '',
    saved: '',
    hydrated: false,
    threadId,
    attIds: [] as number[],
    savedAtts: null as string | null,
  });
  flushRef.current = {
    body: bodyHtml,
    text: bodyText,
    saved: draftQ.data?.body ?? '',
    hydrated,
    threadId,
    attIds: att.readyIds,
    savedAtts: lastSavedAttsRef.current,
  };
  // Discard-in-progress: blocks the unmount flush from resurrecting a draft
  // the user just deleted (state updates may not reach flushRef before close).
  const discardedRef = useRef(false);
  useEffect(() => {
    return () => {
      if (discardedRef.current) return;
      const f = flushRef.current;
      // Blank-and-never-had-content: nothing to flush, and writing '' could
      // erase a server-restored draft (see the autosave guard).
      if (!f.text.trim() && f.attIds.length === 0 && !everHadContentRef.current) return;
      const bodyUnchanged = f.body === f.saved;
      const attsUnchanged = f.savedAtts === null || f.savedAtts === f.attIds.join(',');
      if (!f.hydrated || (bodyUnchanged && attsUnchanged)) return;
      void fetch(`/api/threads/${f.threadId}/draft`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body: f.body, attachmentIds: f.attIds }),
        keepalive: true,
      }).catch(() => {});
    };
  }, []);

  /** Discard draft: wipe the text, delete the server draft AND its uploaded
   *  files, close the composer. The one exit that does NOT save. */
  function handleDiscard() {
    if (
      (bodyText.trim() || att.uploads.length > 0) &&
      !confirm('Discard this draft? The text and its attachments will be deleted.')
    ) {
      return;
    }
    discardedRef.current = true;
    att.discardAll();
    void fetch(`/api/threads/${threadId}/draft`, {
      method: 'DELETE',
      credentials: 'include',
    }).finally(() => {
      qc.setQueryData(['thread-draft', threadId], {
        body: '',
        attachments: [],
        updatedAt: null,
        peers: [],
      });
      void qc.invalidateQueries({ queryKey: ['thread-draft', threadId] });
    });
    rteRef.current?.setHtml('');
    lastSavedAttsRef.current = '';
    toast.success('Draft discarded');
    onDiscarded?.();
  }

  function submit(opts: { scheduledAt?: string; force?: boolean } = {}) {
    const trimmed = bodyText.trim();
    if (!trimmed) return;
    if (editableRecipients && to.length === 0) {
      toast.info('Add at least one recipient');
      return;
    }
    if (att.anyUploading) {
      toast.info('Hold on — attachment still uploading');
      return;
    }
    if (!opts.force) {
      const warnings = preSendWarnings({
        bodyText,
        recipients: editableRecipients ? [...to, ...cc] : (initialTo ?? []),
        names: knownNames,
        attachmentCount: att.readyIds.length,
      });
      if (!confirmPreSend(warnings)) return;
    }
    send.mutate(
      {
        body: trimmed,
        bodyHtml,
        signatureId: sigId,
        fromAccountId: fromAccountId !== accountId ? fromAccountId : undefined,
        replyAll,
        // Explicit recipients only in compose-style mode; otherwise the server
        // derives them from the thread.
        to: editableRecipients ? to : undefined,
        cc: editableRecipients ? cc : undefined,
        bcc: editableRecipients ? bcc : undefined,
        archiveAfterSend: true,
        scheduledAt: opts.scheduledAt,
        attachmentIds: att.readyIds,
        lastSeenMessageId: lastSeenAtOpen,
        force: opts.force,
      },
      {
        onError: (err) => {
          const tc = (err as Error & { threadChanged?: ThreadChangedInfo }).threadChanged;
          if (tc) {
            // Collision guard: tell the user exactly what happened and let
            // them decide — this is the anti-double-reply moment.
            const what: string[] = [];
            if (tc.teammateReply) what.push(`${tc.teammateReply.by} already sent a reply on this thread`);
            if (tc.newMessage) what.push(`a new message from ${tc.newMessage.from} just arrived`);
            const ok = confirm(
              `Hold on — ${what.join(', and ')} while you were writing.\n\n` +
                `Check the thread before sending a second answer.\n\nSend your reply anyway?`,
            );
            if (ok) submit({ ...opts, force: true });
            return;
          }
          toast.error(err instanceof Error ? err.message : 'Send failed');
        },
        onSuccess: (resp) => {
          // Optimistic: yank the thread row from every cached list so the
          // inbox feels archived immediately. The worker actually archives
          // when send_at fires; if the user undoes we restore the snapshot.
          const snapshot = qc.getQueriesData<unknown>({ queryKey: ['threads'] });
          qc.setQueriesData<{ pages?: { threads: { id: number }[] }[] }>(
            { queryKey: ['threads'] },
            (old) => {
              if (!old?.pages) return old;
              return {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  threads: p.threads.filter((t) => t.id !== threadId),
                })),
              } as typeof old;
            },
          );

          // Surface the 'Sending…' row in Sent immediately.
          void qc.invalidateQueries({ queryKey: ['outgoing'] });
          // Snapshot completed uploads BEFORE clearing — Undo brings them back
          // (their server rows get un-consumed by the cancel).
          const uploadsSnapshot = att.uploads.filter((u) => u.status === 'done');
          const bodyHtmlSnapshot = bodyHtml;
          rteRef.current?.clear();
          setReplyAll(false);
          setSaveState('idle');
          att.clear();
          // Don't trigger clearDraft.mutate here — the server already
          // removed the draft row when it accepted the outgoing send. But DO
          // zero the cached copy right now: it otherwise still holds the full
          // sent message until the next poll, and anything reading it in that
          // window (the auto-open-reply effect) would re-open a composer with
          // a copy of the message that just went out — then autosave it back
          // as a draft. That was the "2 copies of my message" bug.
          qc.setQueryData<ThreadDraft>(['thread-draft', threadId], (old) => ({
            body: '',
            updatedAt: null,
            peers: old?.peers ?? [],
          }));
          onSent?.();

          // Future-scheduled sends show a 'Scheduled for …' toast (no undo
          // button — they'll cancel from the Outgoing list); immediate sends
          // get the classic Gmail-style undo toast that holds for 30 s.
          const isScheduled = opts.scheduledAt && new Date(opts.scheduledAt).getTime() > Date.now() + 35_000;
          if (isScheduled) {
            toast.success(`Scheduled for ${formatSendAt(resp.sendAt)}`, {
              duration: 6_000,
              action: {
                label: 'Cancel',
                onClick: () => {
                  cancelOutgoing.mutate(resp.outgoingId, {
                    onSuccess: () => {
                      for (const [key, data] of snapshot) qc.setQueryData(key, data);
                      void qc.invalidateQueries({ queryKey: ['threads'] });
                      void qc.invalidateQueries({ queryKey: ['outgoing'] });
                      void qc.invalidateQueries({ queryKey: ['thread-draft', threadId] });
                      onUndoRestore?.(threadId, uploadsSnapshot, replyAll);
                    },
                  });
                },
              },
            });
          } else {
            toast.success('Sent & archived', {
              duration: 30_000,
              action: {
                label: 'Undo',
                onClick: () => {
                  cancelOutgoing.mutate(resp.outgoingId, {
                    onSuccess: () => {
                      for (const [key, data] of snapshot) qc.setQueryData(key, data);
                      void qc.invalidateQueries({ queryKey: ['threads'] });
                      void qc.invalidateQueries({ queryKey: ['outgoing'] });
                      // Seed the draft cache with the real message NOW, so the
                      // reopened composer hydrates from it instead of from the
                      // zeroed-on-send cache (which it would then autosave over
                      // the server's restored draft — the lost-undo bug).
                      qc.setQueryData<ThreadDraft>(['thread-draft', threadId], (old) => ({
                        body: bodyHtmlSnapshot,
                        attachments: uploadsSnapshot
                          .map((u) => u.uploaded)
                          .filter((u): u is NonNullable<typeof u> => !!u),
                        updatedAt: new Date().toISOString(),
                        peers: old?.peers ?? [],
                      }));
                      void qc.invalidateQueries({ queryKey: ['thread-draft', threadId] });
                      onUndoRestore?.(threadId, uploadsSnapshot, replyAll);
                      toast.info('Send cancelled · draft restored');
                    },
                  });
                },
              },
            });
          }
        },
      },
    );
  }

  const peers = (draftQ.data?.peers ?? []).filter((p) => p.authorUserId);

  return (
    <div
      className={cn(
        'relative',
        inDialog
          ? 'flex min-h-0 flex-1 flex-col px-4 py-3'
          : 'border-t bg-card/30 px-4 py-3 md:px-6',
      )}
      {...replyDrop.rootProps}
    >
      {replyDrop.dragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-primary/60 bg-primary/5">
          <div className="flex items-center gap-2 rounded-md bg-card px-3 py-2 text-sm shadow">
            <Upload className="size-4" strokeWidth={1.75} />
            Drop images inline · files attach
          </div>
        </div>
      )}
      {peers.length > 0 && (
        <div className="mb-2 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Loader2 className="size-3 animate-spin opacity-50" strokeWidth={2} />
          {peers.map((p) => p.authorName || p.authorEmail).join(', ')} is also drafting
        </div>
      )}
      {/* From (send-as) + signature pick — mirrors the new-message composer
          so reply has the same identity controls. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span>From</span>
          <MenuPicker
            variant="inline"
            className="max-w-[240px]"
            value={String(fromAccountId)}
            onChange={(v) => setFromAccountId(Number(v))}
            options={allAccounts.map((a) => ({
              value: String(a.id),
              label: a.nickname ? `${a.nickname} <${a.emailAddress}>` : a.emailAddress,
            }))}
          />
        </div>
        {accountSigs.length > 1 && (
          <div className="flex items-center gap-1.5">
            <span>Signature</span>
            <MenuPicker
              variant="inline"
              value={String(sigId ?? accountSigs[0]!.id)}
              onChange={(v) => setSigId(Number(v))}
              options={accountSigs.map((sg) => ({
                value: String(sg.id),
                label: sg.name + (sg.isDefault ? ' (default)' : ''),
              }))}
            />
          </div>
        )}
        {fromAccountId !== accountId && (
          <span className="text-amber-600 dark:text-amber-400">
            Sending from a different inbox — reply will thread via headers.
          </span>
        )}
      </div>
      {editableRecipients && (
        <div className="mb-2 space-y-1.5 rounded-md border bg-background/40 p-2">
          <div className="flex items-start gap-2">
            <span className="w-9 shrink-0 pt-1.5 text-right text-[11px] text-muted-foreground">To</span>
            <RecipientChips value={to} onChange={setTo} placeholder="recipient@example.com" />
            <div className="flex shrink-0 gap-2 pt-1.5 text-[10px] text-muted-foreground">
              {!showCc && (
                <button type="button" onClick={() => setShowCc(true)} className="hover:text-foreground">
                  Cc
                </button>
              )}
              {!showBcc && (
                <button type="button" onClick={() => setShowBcc(true)} className="hover:text-foreground">
                  Bcc
                </button>
              )}
            </div>
          </div>
          {showCc && (
            <div className="flex items-start gap-2">
              <span className="w-9 shrink-0 pt-1.5 text-right text-[11px] text-muted-foreground">Cc</span>
              <RecipientChips value={cc} onChange={setCc} placeholder="cc@example.com" />
            </div>
          )}
          {showBcc && (
            <div className="flex items-start gap-2">
              <span className="w-9 shrink-0 pt-1.5 text-right text-[11px] text-muted-foreground">Bcc</span>
              <RecipientChips value={bcc} onChange={setBcc} placeholder="bcc@example.com" />
            </div>
          )}
        </div>
      )}
      <RichTextEditor
        ref={rteRef}
        onChange={(v) => {
          setBodyHtml(v.html);
          setBodyText(v.text);
        }}
        placeholder="Reply to this thread…"
        disabled={send.isPending}
        minHeightClass={inDialog ? 'min-h-[40dvh]' : 'min-h-[72px]'}
        onMetaEnter={() => submit()}
      />
      {send.error && (
        <p className="mt-2 text-xs text-destructive">
          {send.error instanceof Error ? send.error.message : 'Send failed'}
        </p>
      )}
      {hasSig && <SignatureInlinePreview accountId={fromAccountId} signatureId={sigId} />}
      {/* Attachment chips */}
      {att.uploads.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {att.uploads.map((u) => (
            <span
              key={u.localId}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]',
                u.status === 'error'
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'bg-card',
              )}
            >
              {u.status === 'uploading' ? (
                <Loader2 className="size-3 animate-spin" strokeWidth={2} />
              ) : (
                <Paperclip className="size-3" strokeWidth={2} />
              )}
              <span className="max-w-[160px] truncate">{u.filename}</span>
              <span className="text-muted-foreground">{formatBytes(u.sizeBytes)}</span>
              {u.error && <span>· {u.error}</span>}
              <button
                type="button"
                onClick={() => att.remove(u.localId)}
                className="rounded p-0.5 opacity-60 hover:opacity-100"
                aria-label="Remove attachment"
              >
                <X className="size-2.5" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) att.addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach files"
              >
                <Paperclip className="size-3.5" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Attach files (max 20 MB each)</TooltipContent>
          </Tooltip>
          <TemplatePicker onInsert={(html) => rteRef.current?.insertHtml(html)} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={handleDiscard}
                aria-label="Discard draft"
              >
                <Trash2 className="size-3.5" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Discard draft (deletes text + attachments)</TooltipContent>
          </Tooltip>
          {!editableRecipients && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <Switch checked={replyAll} onCheckedChange={setReplyAll} />
              Reply all
            </label>
          )}
          <span className="text-[10px] text-muted-foreground">
            {saveState === 'saving' ? 'Saving draft…' : saveState === 'saved' ? 'Draft saved' : ''}
          </span>
        </div>
        <SendSplitButton
          disabled={send.isPending || !bodyText.trim()}
          loading={send.isPending}
          primaryLabel="Send & archive"
          onSendNow={() => submit()}
          onSchedule={(d) => submit({ scheduledAt: d.toISOString() })}
        />
      </div>
    </div>
  );
}

// --- Split-button Send + Schedule -----------------------------------------

// --- Comments panel -------------------------------------------------------

// AI thread summary card — generates on first open, serves from the server
// cache afterwards (re-summarises only when the thread has new messages).
/** Read-only panel for a rule-generated AI reply draft, with a Copy button.
 *  Self-hides when the thread has no draft. */
function ThreadAiDraftCard({ threadId }: { threadId: number }) {
  const q = useQuery<{ draft: string | null; createdAt?: string }>({
    queryKey: ['ai-draft', threadId],
    queryFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/ai-draft`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
  const [copied, setCopied] = useState(false);
  const draft = q.data?.draft;
  if (!draft) return null;
  return (
    <div className="mx-4 mt-4 rounded-lg border border-violet-500/30 bg-violet-500/5 px-4 py-3 md:mx-6">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="size-3.5 shrink-0 text-violet-600 dark:text-violet-400" strokeWidth={2} />
          <span className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
            AI-drafted reply
          </span>
        </div>
        <Button
          variant="ghost"
          size="xs"
          className="gap-1"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(draft);
              setCopied(true);
              toast.success('Draft copied');
              setTimeout(() => setCopied(false), 1500);
            } catch {
              toast.error('Copy failed');
            }
          }}
        >
          {copied ? <Check className="size-3" strokeWidth={2} /> : <Copy className="size-3" strokeWidth={2} />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="whitespace-pre-wrap text-sm text-foreground/90">{draft}</p>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Suggested by a rule — review before sending.
      </p>
    </div>
  );
}

function ThreadSummaryCard({ threadId }: { threadId: number }) {
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'done'; summary: string; cached: boolean }
    | { phase: 'error'; message: string }
  >({ phase: 'loading' });

  const generate = useCallback(
    (force: boolean) => {
      setState({ phase: 'loading' });
      fetch(`/api/threads/${threadId}/summary`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ force }),
      })
        .then(async (r) => {
          if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            throw new Error(j.detail || j.error || `HTTP ${r.status}`);
          }
          return r.json() as Promise<{ summary: string; cached: boolean }>;
        })
        .then((j) => setState({ phase: 'done', summary: j.summary, cached: j.cached }))
        .catch((e) =>
          setState({ phase: 'error', message: e instanceof Error ? e.message : 'Failed' }),
        );
    },
    [threadId],
  );

  useEffect(() => {
    generate(false);
  }, [generate]);

  return (
    <div className="mx-4 mt-4 rounded-lg border border-violet-500/30 bg-violet-500/5 px-4 py-3 md:mx-6">
      <div className="flex items-center gap-2">
        <Sparkles className="size-3.5 shrink-0 text-violet-600 dark:text-violet-400" strokeWidth={2} />
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          AI summary
        </span>
        {state.phase === 'done' && (
          <button
            type="button"
            onClick={() => generate(true)}
            className="ml-auto rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-violet-500/10 hover:text-foreground"
            title="Regenerate"
          >
            Regenerate
          </button>
        )}
      </div>
      <div className="mt-2 text-sm">
        {state.phase === 'loading' && (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" strokeWidth={2} />
            Summarising thread…
          </span>
        )}
        {state.phase === 'error' && (
          <span className="text-destructive">Couldn&rsquo;t summarise: {state.message}</span>
        )}
        {state.phase === 'done' && (
          <div className="whitespace-pre-wrap leading-relaxed">{state.summary}</div>
        )}
      </div>
    </div>
  );
}

// Always-visible internal-note input at the bottom of the reading pane.
// Amber tint = the industry convention for 'internal, never sent to the
// customer' (Missive, Front, Intercom all use yellow for notes).
// --- @mention autocomplete for the internal comment boxes ------------------
// Mentions are matched server-side on the email local-part (@dileep for
// dileep@…), so that's exactly what we insert. Drives a dropdown over a plain
// <input> or <textarea>; the consumer spreads the returned handlers + renders
// `dropdown` inside a `relative` wrapper.
function useMentionAutocomplete<T extends HTMLInputElement | HTMLTextAreaElement>(opts: {
  value: string;
  onChange: (v: string) => void;
  inputRef: React.RefObject<T | null>;
}) {
  const { value, onChange, inputRef } = opts;
  const usersQ = useWorkspaceUsers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const anchorRef = useRef(0); // index of the '@' that opened the menu

  const candidates = useMemo(() => {
    if (!open) return [] as WorkspaceUser[];
    const q = query.toLowerCase();
    return (usersQ.data?.users ?? [])
      .filter((u) => {
        const local = (u.email.split('@')[0] ?? '').toLowerCase();
        return !q || local.includes(q) || (u.name ?? '').toLowerCase().includes(q);
      })
      .slice(0, 6);
  }, [open, query, usersQ.data]);

  useEffect(() => {
    if (activeIdx >= candidates.length) setActiveIdx(0);
  }, [candidates.length, activeIdx]);

  // Detect an @token immediately before the caret; open/close the menu.
  function syncFromCaret(nextValue = value) {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? nextValue.length;
    const m = nextValue.slice(0, caret).match(/(?:^|\s)@([A-Za-z0-9._-]*)$/);
    if (m) {
      anchorRef.current = caret - (m[1]?.length ?? 0) - 1;
      setQuery(m[1] ?? '');
      setOpen(true);
    } else {
      setOpen(false);
    }
  }

  function handleChange(v: string) {
    onChange(v);
    queueMicrotask(() => syncFromCaret(v));
  }

  function pick(user: WorkspaceUser) {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? value.length;
    const local = user.email.split('@')[0] ?? user.email;
    const start = anchorRef.current;
    const next = value.slice(0, start) + '@' + local + ' ' + value.slice(caret);
    onChange(next);
    setOpen(false);
    const newCaret = start + local.length + 2;
    queueMicrotask(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
      }
    });
  }

  // Returns true when the menu consumed the key (consumer should not also act).
  function onKeyDown(e: React.KeyboardEvent): boolean {
    if (!open || candidates.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % candidates.length);
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + candidates.length) % candidates.length);
      return true;
    }
    if (e.key === 'Tab' || (e.key === 'Enter' && !e.metaKey && !e.ctrlKey)) {
      const u = candidates[activeIdx];
      if (u) {
        e.preventDefault();
        pick(u);
        return true;
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return true;
    }
    return false;
  }

  const dropdown =
    open && candidates.length > 0 ? (
      <div className="absolute bottom-full left-0 z-50 mb-1 w-64 overflow-hidden rounded-md border bg-popover shadow-md">
        {candidates.map((u, i) => {
          const local = u.email.split('@')[0] ?? u.email;
          return (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(u);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm',
                i === activeIdx ? 'bg-accent' : 'hover:bg-accent/60',
              )}
            >
              <span className="font-medium">@{local}</span>
              {u.name && <span className="truncate text-xs text-muted-foreground">{u.name}</span>}
            </button>
          );
        })}
      </div>
    ) : null;

  return { onKeyDown, handleChange, dropdown, syncFromCaret };
}

function TeamCommentBox({
  threadId,
  onViewComments,
}: {
  threadId: number;
  onViewComments: () => void;
}) {
  const add = useAddComment(threadId);
  const commentsQ = useThreadComments(threadId);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const mentions = useMentionAutocomplete({ value, onChange: setValue, inputRef });
  const count = commentsQ.data?.comments.length ?? 0;

  function post() {
    const trimmed = value.trim();
    if (!trimmed || add.isPending) return;
    add.mutate(trimmed, {
      onSuccess: () => {
        setValue('');
        toast.success('Comment posted to the team');
      },
    });
  }

  return (
    <div className="border-t border-amber-500/30 bg-amber-500/10 px-6 py-2">
      <div className="flex items-center gap-2">
        <MessageSquare className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={2} />
        <div className="relative min-w-0 flex-1">
          {mentions.dropdown}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => mentions.handleChange(e.target.value)}
            onKeyUp={() => mentions.syncFromCaret()}
            onClick={() => mentions.syncFromCaret()}
            placeholder="Comment to your team — @mention a teammate, never sent to the customer…"
            disabled={add.isPending}
            className="w-full min-w-0 bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground/60"
            onKeyDown={(e) => {
              if (mentions.onKeyDown(e)) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                post();
              }
            }}
          />
        </div>
        {count > 0 && (
          <button
            type="button"
            onClick={onViewComments}
            className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
            title="View all comments"
          >
            {count} {count === 1 ? 'comment' : 'comments'}
          </button>
        )}
        {add.isPending && <Loader2 className="size-3.5 shrink-0 animate-spin text-amber-600" />}
      </div>
    </div>
  );
}

function CommentCountBadge({ threadId }: { threadId: number }) {
  const q = useThreadComments(threadId);
  const n = q.data?.comments.length ?? 0;
  if (!n) return null;
  return <span className="text-[10px] text-muted-foreground">{n}</span>;
}

const ACTIVITY_LABEL: Record<string, string> = {
  'thread.done': 'marked this done',
  'thread.reopen': 'reopened this',
  'thread.trash': 'trashed this',
  'thread.untrash': 'restored this from trash',
  'thread.spam': 'reported this as spam',
  'thread.unsnooze': 'unsnoozed this',
  'comment.delete': 'deleted a comment',
};

function prettyCategory(c: string): string {
  const words = c.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Who performed an action — AI + rule rows have no user, so label them. */
function activityActor(e: ActivityEntry): string {
  if (e.action.startsWith('ai.')) return 'AI Categoriser';
  if (e.action.startsWith('rule.')) return 'Filter rule';
  return e.actor;
}

function describeActivity(e: ActivityEntry): string {
  if (e.action === 'thread.snooze') {
    const until = typeof e.meta.until === 'string' ? new Date(e.meta.until) : null;
    return until && !Number.isNaN(until.getTime())
      ? `snoozed this until ${until.toLocaleString()}`
      : 'snoozed this';
  }
  if (e.action === 'domain.block') {
    return typeof e.meta.domain === 'string' ? `blocked ${e.meta.domain}` : 'blocked the sender domain';
  }
  if (e.action === 'ai.triage.muted') {
    const cat = typeof e.meta.category === 'string' ? e.meta.category : null;
    return cat ? `muted the notification · ${prettyCategory(cat)}` : 'muted the notification';
  }
  if (e.action === 'ai.triage.classified') {
    const cat = typeof e.meta.category === 'string' ? e.meta.category : null;
    return cat ? `categorised this as ${prettyCategory(cat)}` : 'categorised this';
  }
  if (e.action === 'rule.muted' || e.action === 'rule.notified') {
    const forWhom = e.meta.scope === 'user' ? ' for one person' : '';
    const name = typeof e.meta.ruleName === 'string' ? ` · ${e.meta.ruleName}` : '';
    const verb = e.action === 'rule.muted' ? 'muted the notification' : 'let this notify';
    return `${verb}${forWhom}${name}`;
  }
  return ACTIVITY_LABEL[e.action] ?? e.action;
}

/** Live collision warning: teammates who have this thread open or are drafting
 *  a reply right now. Drafting (from the shared-draft peers) is the stronger
 *  signal and wins the wording. Amber — this is a "coordinate before you both
 *  answer the customer" nudge. */
function CollisionBanner({ threadId }: { threadId: number }) {
  const presenceQ = useThreadPresence(threadId);
  const draftQ = useThreadDraft(threadId); // same query key as the composer's — cached, no extra request
  const drafting = (draftQ.data?.peers ?? []).filter((p) => p.authorUserId);
  const draftingIds = new Set(drafting.map((p) => p.authorUserId));
  const viewing = (presenceQ.data?.viewers ?? []).filter((v) => !draftingIds.has(v.userId));
  if (drafting.length === 0 && viewing.length === 0) return null;
  const parts: string[] = [];
  if (drafting.length > 0) {
    parts.push(
      `${drafting.map((p) => p.authorName || p.authorEmail).join(', ')} is writing a reply`,
    );
  }
  if (viewing.length > 0) {
    parts.push(`${viewing.map((v) => v.name).join(', ')} is viewing`);
  }
  return (
    <div className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-[12px] text-amber-800 md:px-6 dark:text-amber-300">
      <Users className="size-3.5 shrink-0" strokeWidth={2} />
      <span>
        {parts.join(' · ')}
        {drafting.length > 0 ? ' — coordinate before replying' : ''}
      </span>
    </div>
  );
}

/** Compact "who did what" strip shown at the top of an open thread — the most
 *  recent AI + human actions, so provenance is visible without the Activity tab. */
function ThreadProvenance({ threadId }: { threadId: number }) {
  const q = useThreadActivity(threadId);
  const entries = (q.data?.activity ?? []).slice(0, 3);
  if (entries.length === 0) return null;
  return (
    <div className="border-b bg-muted/20 px-4 py-1.5 md:px-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {entries.map((e) => {
          const isAi = e.action.startsWith('ai.');
          return (
            <span key={e.id} className="inline-flex items-center gap-1">
              {isAi ? (
                <Sparkles className="size-3 shrink-0 text-violet-500" strokeWidth={2} />
              ) : (
                <History className="size-3 shrink-0" strokeWidth={2} />
              )}
              <span className="font-medium text-foreground/80">{activityActor(e)}</span>
              <span>{describeActivity(e)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Per-thread activity feed — reads the audit log (done/snooze/trash/block/…). */
function ActivityFeed({ threadId }: { threadId: number }) {
  const q = useThreadActivity(threadId);
  const entries = q.data?.activity ?? [];
  return (
    <ScrollArea className="flex-1">
      <div className="space-y-3 px-4 py-4 md:px-6">
        {q.isLoading && <p className="text-sm text-muted-foreground">Loading activity…</p>}
        {!q.isLoading && entries.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No activity yet. Actions like done, snooze, trash and block show up here.
          </p>
        )}
        {entries.map((e) => (
          <div key={e.id} className="flex items-start gap-2.5 text-sm">
            <History className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <div className="min-w-0">
              <div>
                <span className="font-medium">{activityActor(e)}</span>{' '}
                <span className="text-muted-foreground">{describeActivity(e)}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {new Date(e.createdAt).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

function CommentsPanel({ threadId }: { threadId: number }) {
  const q = useThreadComments(threadId);
  const add = useAddComment(threadId);
  const del = useDeleteComment(threadId);
  const me = useMe();
  const [body, setBody] = useState('');
  const textRef = useRef<HTMLTextAreaElement>(null);
  const mentions = useMentionAutocomplete({ value: body, onChange: setBody, inputRef: textRef });
  const comments = q.data?.comments ?? [];

  function submit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    add.mutate(trimmed, { onSuccess: () => setBody('') });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="flex-1">
        <div className="space-y-3 px-4 py-4 md:px-6">
          {q.isLoading && (
            <p className="text-sm text-muted-foreground">Loading comments…</p>
          )}
          {!q.isLoading && comments.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No internal comments yet. Drop a note for the team — these stay private.
            </p>
          )}
          {comments.map((c) => (
            <CommentRow
              key={c.id}
              c={c}
              canDelete={me.data?.user.id === c.authorUserId}
              onDelete={() => del.mutate(c.id)}
            />
          ))}
        </div>
      </ScrollArea>
      <div className="border-t bg-card/30 px-4 py-3 md:px-6">
        <div className="relative">
          {mentions.dropdown}
          <Textarea
            ref={textRef}
            value={body}
            onChange={(e) => mentions.handleChange(e.target.value)}
            onKeyUp={() => mentions.syncFromCaret()}
            onClick={() => mentions.syncFromCaret()}
            placeholder="Internal comment — @mention a teammate to notify them. Not sent outside the team."
            rows={2}
            disabled={add.isPending}
            className="resize-none bg-background"
            onKeyDown={(e) => {
              if (mentions.onKeyDown(e)) return;
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
          />
        </div>
        {add.error && (
          <p className="mt-2 text-xs text-destructive">
            {add.error instanceof Error ? add.error.message : 'Failed to post'}
          </p>
        )}
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            disabled={add.isPending || !body.trim()}
            onClick={submit}
            className="gap-1.5"
          >
            {add.isPending ? (
              <Loader2 className="size-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <MessageSquare className="size-3.5" strokeWidth={2} />
            )}
            Post
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Tag pills + menu -----------------------------------------------------

function ThreadTagPill({
  threadId,
  tag,
}: {
  threadId: number;
  tag: { id: number; name: string; color: string };
}) {
  const remove = useRemoveTag(threadId);
  return (
    <span
      className={cn(
        'group inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
        tagClasses(tag.color),
      )}
    >
      {tag.name}
      <button
        type="button"
        onClick={() => remove.mutate(tag.id)}
        disabled={remove.isPending}
        className="opacity-50 transition-opacity hover:opacity-100"
        title="Remove tag"
      >
        <X className="size-2.5" strokeWidth={2.5} />
      </button>
    </span>
  );
}

function TagMenu({ thread }: { thread: { id: number; tags: { id: number; name: string; color: string }[] } }) {
  const tagsQ = useTags();
  const apply = useApplyTag(thread.id);
  const create = useCreateTag();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<string>('sky');

  const appliedIds = new Set(thread.tags.map((t) => t.id));
  const available = (tagsQ.data?.tags ?? []).filter((t) => !appliedIds.has(t.id));

  function submitCreate() {
    const name = newName.trim();
    if (!name) return;
    create.mutate(
      { name, color: newColor },
      {
        onSuccess: ({ tag }) => {
          apply.mutate(tag.id);
          setNewName('');
          setCreating(false);
        },
      },
    );
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          setCreating(false);
          setNewName('');
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5">
          <TagIcon className="size-3.5" strokeWidth={1.75} />
          Tag
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {available.length === 0 && !creating && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            All tags applied. Create a new one below.
          </div>
        )}
        {available.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onClick={() => apply.mutate(t.id)}
            className="gap-2"
          >
            <span className={cn('size-2.5 rounded-full', tagDotClass(t.color))} />
            <span className="flex-1 truncate">{t.name}</span>
          </DropdownMenuItem>
        ))}
        <div className="border-t" />
        {!creating ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setCreating(true);
            }}
          >
            <Plus className="size-3.5" strokeWidth={2} />
            New tag…
          </DropdownMenuItem>
        ) : (
          <div className="space-y-2 p-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Tag name"
              autoFocus
              maxLength={64}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitCreate();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setCreating(false);
                }
              }}
            />
            <div className="flex flex-wrap gap-1">
              {TAG_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={cn(
                    'size-5 rounded-full ring-2 ring-transparent transition',
                    tagDotClass(c),
                    newColor === c && 'ring-foreground/40',
                  )}
                  title={c}
                />
              ))}
            </div>
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setCreating(false)}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={submitCreate}
                disabled={!newName.trim() || create.isPending}
              >
                {create.isPending ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  'Create'
                )}
              </Button>
            </div>
            {create.error && (
              <p className="text-[10px] text-destructive">
                {create.error instanceof Error ? create.error.message : 'Failed'}
              </p>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CommentRow({
  c,
  canDelete,
  onDelete,
}: {
  c: ThreadComment;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const initials = (c.authorName || c.authorEmail)
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className="flex gap-3">
      <Avatar className="size-7 shrink-0">
        {c.authorPicture && <AvatarImage src={c.authorPicture} alt={c.authorName} />}
        <AvatarFallback className="text-[10px]">{initials || '?'}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 rounded-md border bg-card px-3 py-2">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
          <span className="font-medium text-foreground">{c.authorName || c.authorEmail}</span>
          <div className="flex items-center gap-1 text-muted-foreground">
            <span>{new Date(c.createdAt).toLocaleString()}</span>
            {canDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onDelete}
                    className="rounded p-0.5 hover:bg-secondary hover:text-foreground"
                  >
                    <Trash2 className="size-3" strokeWidth={1.75} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="whitespace-pre-wrap break-words text-sm">
          {renderWithMentions(c.body)}
        </div>
      </div>
    </div>
  );
}

function MessageCard({
  m,
  defaultCollapsed = false,
}: {
  m: ThreadMessage;
  /** True for every message except the most recent — Gmail-style. Older
   *  messages render as a tappable one-line summary; the iframe body
   *  doesn't mount until the user expands. Big perf win on long threads. */
  defaultCollapsed?: boolean;
}) {
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  if (!expanded) {
    // 1-line preview snippet — strip tags and collapse whitespace so a
    // signature-heavy body still shows the meat.
    const preview = (m.bodyText ?? stripHtmlTags(m.bodyHtml ?? ''))
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group block w-full rounded-md border bg-card/40 px-4 py-3 text-left transition-colors hover:bg-card"
        aria-label="Expand message"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="truncate font-medium text-foreground">
                {m.from ?? '(unknown sender)'}
              </span>
              <span
                className="shrink-0 whitespace-nowrap tabular-nums text-muted-foreground"
                title={new Date(m.sentAt).toLocaleString()}
              >
                {formatRelative(m.sentAt)}
              </span>
            </div>
            {preview && (
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {preview}
              </div>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <article className="rounded-md border bg-card p-4 shadow-sm">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-2 text-xs text-muted-foreground">
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-foreground">{m.from ?? '(unknown sender)'}</span>
            <span>{new Date(m.sentAt).toLocaleString()}</span>
          </div>
          {m.to.length > 0 && <div className="truncate">to: {m.to.join(', ')}</div>}
          {m.cc.length > 0 && <div className="truncate">cc: {m.cc.join(', ')}</div>}
        </div>
        {/* Show a collapse button only on messages that were collapsed by
            default (i.e. older than the most recent). Re-collapsing the
            latest message is rarely useful. */}
        {defaultCollapsed && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Collapse message"
            title="Collapse"
          >
            <X className="size-3.5" strokeWidth={2} />
          </button>
        )}
      </header>
      <Separator className="mb-3" />
      <MessageBody m={m} />
      {(m.attachments?.length ?? 0) > 0 &&
        (() => {
          // Attached images render as inline previews (many clients — Outlook
          // iOS especially — send photos as plain attachments with no cid:
          // reference, so the body alone never shows them). Everything else
          // stays a chip.
          const isImg = (mt?: string) => /^image\/(png|jpe?g|gif|webp)/i.test(mt || '');
          const images = m.attachments!.filter((a) => isImg(a.mimeType));
          const files = m.attachments!.filter((a) => !isImg(a.mimeType));
          return (
            <div className="mt-3 space-y-2 border-t pt-3">
              {images.map((a) => (
                <a
                  key={a.index}
                  href={`/api/messages/${m.id}/attachments/${a.index}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${a.filename} (${formatBytes(a.sizeBytes)}) — opens full size`}
                  className="block w-fit"
                >
                  <img
                    src={`/api/messages/${m.id}/attachments/${a.index}`}
                    alt={a.filename}
                    loading="lazy"
                    className="max-h-80 max-w-full rounded-lg border object-contain"
                  />
                </a>
              ))}
              {files.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {files.map((a) => {
                    // Types the browser can render open in a new tab (the
                    // server serves them inline); the rest stay downloads.
                    const viewable = /^(application\/pdf|text\/plain|message\/rfc822)/i.test(
                      a.mimeType || '',
                    );
                    return (
                      <a
                        key={a.index}
                        href={`/api/messages/${m.id}/attachments/${a.index}`}
                        {...(viewable
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : { download: a.filename })}
                        title={viewable ? 'Opens in a new tab' : 'Download'}
                        className="inline-flex items-center gap-1.5 rounded-md border bg-secondary/50 px-2.5 py-1.5 text-xs transition-colors hover:bg-secondary"
                      >
                        {viewable ? (
                          <Eye className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                        ) : (
                          <Paperclip
                            className="size-3.5 shrink-0 text-muted-foreground"
                            strokeWidth={1.75}
                          />
                        )}
                        <span className="max-w-[220px] truncate font-medium">{a.filename}</span>
                        <span className="text-muted-foreground">{formatBytes(a.sizeBytes)}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
    </article>
  );
}

function MessageBody({ m }: { m: ThreadMessage }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(200);

  // Process the email HTML once: strip script/handler attrs, force every
  // <a> to open in a new tab, and append phishing warnings where the link's
  // visible text claims a domain that isn't the actual href host.
  const processed = useMemo(() => {
    if (!m.bodyHtml) return null;
    return processEmailHtml(m.bodyHtml);
  }, [m.bodyHtml]);

  const srcdoc = useMemo(() => (processed ? buildEmailSrcdoc(processed) : ''), [processed]);

  // The sandboxed iframe inside our srcdoc posts back its scrollHeight; we use
  // that to grow the iframe to fit the email so the reading pane scrolls
  // naturally instead of producing nested scrollbars.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const data = e.data as { type?: string; height?: number } | null;
      if (data?.type === 'wharf-iframe-size' && typeof data.height === 'number') {
        // Clamp to a sane range so a malformed email can't blow up the page.
        setHeight(Math.max(60, Math.min(data.height + 16, 100_000)));
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (m.bodyHtml && srcdoc) {
    return (
      <iframe
        ref={iframeRef}
        title="email body"
        srcDoc={srcdoc}
        // allow-scripts: needed for the inline size-reporting script we inject.
        // allow-popups + allow-popups-to-escape-sandbox: target=_blank links open.
        // No allow-same-origin — keeps the email's DOM walled off from ours.
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        className="block w-full rounded-md bg-white"
        style={{ height, border: 0 }}
      />
    );
  }
  if (m.bodyText) {
    return <pre className="whitespace-pre-wrap font-sans text-sm">{m.bodyText}</pre>;
  }
  return <p className="text-sm italic text-muted-foreground">(empty body)</p>;
}

// --- Email-HTML preprocessing --------------------------------------------

interface ProcessedEmail {
  html: string;
  /** Number of remote images we hid (used to render the 'Show images' banner). */
  blockedImageCount: number;
}

// Registrable domains whose images we load automatically instead of hiding
// behind 'Show images'. These are content/avatar CDNs from major providers
// that serve real images (logos, avatars, screenshots) rather than per-open
// tracking pixels — so the privacy cost of auto-loading is low. Subdomains
// match (e.g. avatars.githubusercontent.com). Edit this list to taste; no UI.
const IMAGE_HOST_ALLOWLIST = [
  'githubusercontent.com', // GitHub avatars, raw user content, camo-proxied images
  'gravatar.com', // Gravatar avatars (used by countless services)
  'googleusercontent.com', // Google profile photos, Drive thumbnails, Gmail proxy
  'gstatic.com', // Google static assets
  'ytimg.com', // YouTube thumbnails
  'slack-edge.com', // Slack avatars + file thumbnails
  'slack.com',
  'atlassian.com', // Jira / Confluence / Trello content
  'wikimedia.org', // Wikipedia / Wikimedia media
];

/** True if a remote image URL is hosted on an allowlisted, known-safe domain
 *  (exact registrable domain or any subdomain of it). */
function isAllowedImageHost(url: string): boolean {
  const host = getHostFromHref(url);
  if (!host) return false;
  return IMAGE_HOST_ALLOWLIST.some((d) => host === d || host.endsWith('.' + d));
}

// URL-scheme allowlists for email attributes. href gets navigable schemes
// only; src also allows data:/cid: for inline images. A URL with no scheme
// (relative, anchor, scheme-relative //host) passes untouched.
const SAFE_HREF_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);
const SAFE_SRC_SCHEMES = new Set(['http', 'https', 'data', 'cid']);
function urlScheme(value: string): string | null {
  const m = /^\s*([a-z][a-z0-9+.-]*):/i.exec(value);
  return m && m[1] ? m[1].toLowerCase() : null;
}

/** Strip scripts/handlers, force-open links in a new tab, flag phishing-y
 *  link text, collapse quoted history under a <details>, and replace remote
 *  http(s) image srcs with data-wharf-src so they can be loaded on demand. */
function processEmailHtml(html: string): ProcessedEmail {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Hard kill anything that can execute code.
  doc.querySelectorAll('script, object, embed, iframe, link, meta').forEach((n) => n.remove());
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      if (name === 'srcdoc') el.removeAttribute(attr.name);
      // Scheme allowlist on URL attributes — strips javascript:, data:text/html,
      // vbscript:, blob:, file:, etc. (previously only javascript: was caught).
      if (name === 'href' || name === 'src') {
        const scheme = urlScheme(attr.value);
        const ok = name === 'href' ? SAFE_HREF_SCHEMES : SAFE_SRC_SCHEMES;
        if (scheme && !ok.has(scheme)) el.removeAttribute(attr.name);
      }
    }
  });

  // Anchors: target=_blank + phishing warning.
  doc.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
    const href = a.getAttribute('href') ?? '';
    const hrefHost = getHostFromHref(href);
    if (!hrefHost) return;
    const text = (a.textContent ?? '').trim();
    if (!text || text.toLowerCase().includes(hrefHost.toLowerCase())) return;
    const textDomain = findDomainInText(text);
    if (!textDomain) return;
    if (domainsMatch(textDomain, hrefHost)) return;
    const warn = doc.createElement('span');
    warn.className = 'wharf-link-warning';
    warn.setAttribute('title', `This link goes to ${hrefHost}, not ${textDomain}`);
    warn.textContent = `⚠ ${hrefHost}`;
    a.parentNode?.insertBefore(warn, a.nextSibling);
  });

  // Collapse quoted history (Gmail's .gmail_quote, Apple Mail / others'
  // blockquote[type="cite"]). Wrap each in a <details> with a summary so
  // the user can expand on demand. Only wrap nodes that have a real
  // preceding sibling — if the whole body is one big quote we leave it
  // alone (forwarded messages, otherwise everything's hidden).
  doc
    .querySelectorAll<HTMLElement>('.gmail_quote, blockquote[type="cite"]')
    .forEach((el) => {
      // Skip nested quotes inside an already-wrapped <details>.
      if (el.closest('details.wharf-quote')) return;
      // Skip if there's no visible content before this quote.
      const parent = el.parentNode;
      if (!parent) return;
      let prevHasContent = false;
      let sibling = el.previousSibling;
      while (sibling) {
        const text = (sibling.textContent ?? '').trim();
        if (text.length > 0) { prevHasContent = true; break; }
        sibling = sibling.previousSibling;
      }
      if (!prevHasContent) return;
      const details = doc.createElement('details');
      details.className = 'wharf-quote';
      const summary = doc.createElement('summary');
      summary.textContent = 'Show quoted text';
      details.appendChild(summary);
      parent.insertBefore(details, el);
      details.appendChild(el);
    });

  // Default-block remote images. data: and cid: srcs (which our sync turns
  // into data: URIs at parse time) are inline + safe — they stay. Images on an
  // allowlisted, known-safe host load automatically. Everything else http(s)
  // goes behind the 'Show images' button.
  let blockedImageCount = 0;
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (/^https?:/i.test(src)) {
      if (isAllowedImageHost(src)) return; // trusted host → load it
      img.setAttribute('data-wharf-src', src);
      img.removeAttribute('src');
      img.classList.add('wharf-blocked-image');
      // Preserve a sensible fallback so layout doesn't collapse to 0.
      if (!img.getAttribute('alt')) img.setAttribute('alt', '');
      blockedImageCount++;
    }
  });
  // Also catch background-image: url(http…) on inline styles.
  doc.querySelectorAll<HTMLElement>('[style*="background-image"]').forEach((el) => {
    const s = el.getAttribute('style') ?? '';
    if (/url\(['"]?https?:/i.test(s)) {
      const m = s.match(/url\(\s*['"]?(https?:\/\/[^'")\s]+)/i);
      if (m?.[1] && isAllowedImageHost(m[1])) return; // trusted host → load it
      el.setAttribute('data-wharf-bg', s);
      el.setAttribute('style', s.replace(/background-image\s*:\s*url\([^)]+\)\s*;?/gi, ''));
      blockedImageCount++;
    }
  });

  return { html: doc.body.innerHTML, blockedImageCount };
}

/** Wrap processed body HTML in a minimal document that gives emails their
 *  natural rendering (their own inline styles win) and reports its size to
 *  the parent so the iframe can auto-fit. */
function emailCspNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function buildEmailSrcdoc(processed: ProcessedEmail): string {
  const { html: bodyHtml, blockedImageCount } = processed;
  const nonce = emailCspNonce();
  const banner =
    blockedImageCount > 0
      ? `<div class="wharf-image-banner" id="wharf-image-banner">
           <span>${blockedImageCount} image${blockedImageCount === 1 ? '' : 's'} hidden for privacy</span>
           <button type="button" id="wharf-show-images-btn">Show images</button>
         </div>`
      : '';
  // Strict CSP inside the sandboxed doc: no scripts except our nonce'd resizer,
  // no network except images, no forms/frames/base. Belt-and-braces on top of
  // the sandbox (which already withholds allow-same-origin) and the tag/scheme
  // stripping in processEmailHtml.
  const csp =
    "default-src 'none'; " +
    'img-src http: https: data: cid:; ' +
    "style-src 'unsafe-inline'; " +
    'font-src data: https:; ' +
    `script-src 'nonce-${nonce}'; ` +
    "frame-src 'none'; media-src http: https: data:; base-uri 'none'; form-action 'none'";
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<base target="_blank">
<style>
  html, body {
    margin: 0;
    padding: 12px 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: rgb(31 41 55);
    background: white;
    word-break: break-word;
  }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: rgb(2 132 199); }
  blockquote { border-left: 3px solid rgb(229 231 235); margin: 0; padding-left: 12px; color: rgb(107 114 128); }
  .wharf-link-warning {
    display: inline-block;
    margin: 0 0.25rem;
    padding: 0 0.375rem;
    border-radius: 0.25rem;
    background: rgb(254 226 226);
    color: rgb(153 27 27);
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.5;
    vertical-align: middle;
    border: 1px solid rgb(252 165 165);
    white-space: nowrap;
  }

  /* Collapsed quoted-history block (Gmail's .gmail_quote, etc.) */
  details.wharf-quote {
    margin: 12px 0;
  }
  details.wharf-quote > summary {
    cursor: pointer;
    list-style: none;
    display: inline-block;
    padding: 2px 10px;
    border-radius: 4px;
    background: rgb(243 244 246);
    color: rgb(75 85 99);
    font-size: 12px;
    font-weight: 500;
    user-select: none;
  }
  details.wharf-quote > summary:hover { background: rgb(229 231 235); color: rgb(31 41 55); }
  details.wharf-quote > summary::-webkit-details-marker { display: none; }
  details.wharf-quote[open] > summary { margin-bottom: 8px; }

  /* Default-blocked remote image placeholder */
  .wharf-blocked-image {
    background: rgb(243 244 246);
    border: 1px dashed rgb(209 213 219);
    min-width: 32px;
    min-height: 24px;
    display: inline-block;
    vertical-align: middle;
  }

  /* 'Show images' privacy banner */
  .wharf-image-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    margin: 0 0 12px;
    background: rgb(254 249 195);
    border: 1px solid rgb(250 204 21);
    border-radius: 6px;
    color: rgb(120 53 15);
    font-size: 12px;
  }
  .wharf-image-banner button {
    cursor: pointer;
    background: rgb(120 53 15);
    color: white;
    border: 0;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
  .wharf-image-banner button:hover { background: rgb(146 64 14); }
</style>
</head>
<body>${banner}${bodyHtml}
<script nonce="${nonce}">
(function () {
  function post() {
    var h = Math.max(
      document.documentElement.scrollHeight,
      document.body ? document.body.scrollHeight : 0
    );
    parent.postMessage({ type: 'wharf-iframe-size', height: h }, '*');
  }
  window.addEventListener('load', post);
  function watchImg(img) {
    if (!img.complete) {
      img.addEventListener('load', post);
      img.addEventListener('error', post);
    }
  }
  document.querySelectorAll('img').forEach(watchImg);
  // Resize when a quoted-history block toggles open/closed.
  document.querySelectorAll('details.wharf-quote').forEach(function (d) {
    d.addEventListener('toggle', post);
  });
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(post).observe(document.body);
  } else {
    setTimeout(post, 50);
    setTimeout(post, 250);
    setTimeout(post, 1000);
  }
  // Expose a single 'load all remote images' hook for the banner button.
  window.__wharfShowImages = function () {
    document.querySelectorAll('img[data-wharf-src]').forEach(function (img) {
      img.src = img.getAttribute('data-wharf-src');
      img.classList.remove('wharf-blocked-image');
      watchImg(img);
    });
    document.querySelectorAll('[data-wharf-bg]').forEach(function (el) {
      el.setAttribute('style', el.getAttribute('data-wharf-bg'));
    });
    var banner = document.getElementById('wharf-image-banner');
    if (banner) banner.remove();
    setTimeout(post, 100);
  };
  // Wire the banner button here (CSP forbids inline onclick handlers).
  var showBtn = document.getElementById('wharf-show-images-btn');
  if (showBtn) showBtn.addEventListener('click', window.__wharfShowImages);
})();
</script>
</body>
</html>`;
}

// --- Phishing-warning helpers --------------------------------------------

/** Return the hostname of a real http(s) URL, lowercased + www-stripped. */
function getHostFromHref(href: string): string | null {
  try {
    const u = new URL(href, 'https://x.invalid');
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** First plausible domain-like substring in visible link text, e.g.
 *  "verify at paypal.com today" → "paypal.com". Lowercased, www-stripped. */
function findDomainInText(text: string): string | null {
  const m = text.match(
    /\b([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)*\.[a-z]{2,})\b/i,
  );
  if (!m) return null;
  // Be conservative: must look like a real domain (label.tld), not e.g. "v1.0".
  const cand = m[1]!.toLowerCase().replace(/^www\./, '');
  if (!/[a-z]/.test(cand.split('.').pop() ?? '')) return null;
  return cand;
}

/** True if two hostnames share the same registrable domain (last two labels).
 *  Treats "mail.google.com" and "google.com" as a match; "paypa1.com" and
 *  "paypal.com" as a mismatch. Naive — doesn't consult the Public Suffix
 *  List — but adequate for catching the common phishing-text trick. */
function domainsMatch(a: string, b: string): boolean {
  if (a === b) return true;
  const aRoot = a.split('.').slice(-2).join('.');
  const bRoot = b.split('.').slice(-2).join('.');
  return aRoot === bRoot;
}

// Render comment body with @mentions highlighted. Plain string in, JSX out.
// Conservative: only matches @ followed by [a-zA-Z0-9._-]+ — won't catch
// "@Dileep Kaluaratchie" (space), only the first token. Good enough for V1.
function renderWithMentions(text: string): ReactNode[] {
  const re = /@([A-Za-z0-9._-]+)/g;
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    out.push(
      <span
        key={key++}
        className="rounded bg-primary/15 px-1 py-0.5 text-primary"
      >
        {m[0]}
      </span>,
    );
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

/** Very simple HTML → plain text for the forward attribution block. */
function stripHtmlTags(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// --- Snooze helpers ------------------------------------------------------

/** Friendly relative-ish format for the snoozed-until label, eg
 *  'today 6:00 PM' / 'tomorrow 8:00 AM' / 'Mon, Jun 2 at 8:00 AM'. */
function formatSnoozeUntil(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `today ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `tomorrow ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${time}`;
}

/** Build the canonical snooze quick-pick options relative to now. Each call
 *  generates fresh Dates so dropdowns always reflect current 'today'. */
function snoozeOptions(): { label: string; sub: string; until: Date }[] {
  const now = new Date();
  const opts: { label: string; sub: string; until: Date }[] = [];

  // Later today: 6 PM if it's before 5 PM, otherwise +3 hours.
  const laterToday = new Date(now);
  if (now.getHours() < 17) {
    laterToday.setHours(18, 0, 0, 0);
  } else {
    laterToday.setHours(now.getHours() + 3, 0, 0, 0);
  }
  opts.push({
    label: 'Later today',
    sub: laterToday.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    until: laterToday,
  });

  // Tomorrow 8 AM.
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  opts.push({
    label: 'Tomorrow',
    sub: tomorrow.toLocaleString(undefined, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }),
    until: tomorrow,
  });

  // This weekend: next Saturday 8 AM (skip if already weekend).
  const sat = new Date(now);
  const daysToSat = (6 - sat.getDay() + 7) % 7 || 7;
  sat.setDate(sat.getDate() + daysToSat);
  sat.setHours(8, 0, 0, 0);
  opts.push({
    label: 'This weekend',
    sub: sat.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    until: sat,
  });

  // Next week: next Monday 8 AM.
  const mon = new Date(now);
  const daysToMon = (1 - mon.getDay() + 7) % 7 || 7;
  mon.setDate(mon.getDate() + daysToMon);
  mon.setHours(8, 0, 0, 0);
  opts.push({
    label: 'Next week',
    sub: mon.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    until: mon,
  });

  return opts;
}

function SnoozeMenu({ busy, onPick }: { busy: boolean; onPick: (until: Date) => void }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState(() => {
    // Default the custom picker to tomorrow 8 AM in local-time format.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    return toDatetimeLocal(d);
  });
  const opts = snoozeOptions();
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setCustomOpen(false);
      }}
    >
      {/* Nesting matters: Tooltip (context) must wrap the trigger chain.
          DropdownMenuTrigger asChild around <Tooltip> put the menu's click
          handlers on a context provider that renders no DOM — the button
          looked fine but the menu could never open. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={busy}
              aria-label="Snooze"
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Clock className="size-4" strokeWidth={1.75} />
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Snooze</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64">
        {!customOpen && (
          <>
            {opts.map((o) => (
              <DropdownMenuItem
                key={o.label}
                onClick={() => onPick(o.until)}
                className="flex items-center justify-between gap-3"
              >
                <span>{o.label}</span>
                <span className="text-[10px] text-muted-foreground">{o.sub}</span>
              </DropdownMenuItem>
            ))}
            <div className="border-t" />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setCustomOpen(true);
              }}
              className="gap-2"
            >
              <Clock className="size-3.5" strokeWidth={1.75} />
              Pick date &amp; time…
            </DropdownMenuItem>
          </>
        )}
        {customOpen && (
          <div className="space-y-2 p-2">
            <input
              type="datetime-local"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="xs" onClick={() => setCustomOpen(false)}>
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={() => {
                  const d = new Date(customValue);
                  if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now() + 30_000) {
                    onPick(d);
                  }
                }}
              >
                Snooze
              </Button>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** 'tech@techgeneration.uk' → 'tech@' for the compact 'also in' badge. */
/** A compact "which inbox" indicator for a thread row. */
interface AccountChip {
  label: string;
  color: string;
  email: string;
}

/**
 * Colour for an account chip: the shared-inbox nickname colour if set, else a
 * stable colour derived from the address so each inbox reads the same hue every
 * time (slate/gray dropped to keep chips vivid).
 */
function accountChipColor(a: GmailAccount): string {
  if (a.nicknameColor) return a.nicknameColor;
  const palette = TAG_COLORS.slice(2);
  let h = 0;
  for (let i = 0; i < a.emailAddress.length; i++) {
    h = (h * 31 + a.emailAddress.charCodeAt(i)) >>> 0;
  }
  return palette[h % palette.length]!;
}

function shortInbox(email: string): string {
  const local = email.split('@')[0] ?? email;
  return `${local}@`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// --- Sidebar account row + share button -----------------------------------

function SidebarAccountRow({
  a,
  active,
  onSelect,
}: {
  a: GmailAccount;
  active: boolean;
  onSelect: () => void;
}) {
  const me = useMe();
  const [shareOpen, setShareOpen] = useState(false);
  const Icon = a.kind === 'shared' ? Users : Mail;
  const isOwner = me.data?.user.id === a.ownerUserId;

  return (
    <>
      <div
        className={cn(
          'group/row flex w-full items-center gap-1 rounded-md transition-colors',
          active ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50',
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm"
        >
          <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <span className="min-w-0 flex-1 truncate">{a.emailAddress}</span>
          {a.kind === 'shared' && (
            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[9px]">
              shared
            </Badge>
          )}
        </button>
        {/* Hover-revealed share trigger. md:opacity-0 → 100 on hover keeps
            the row clean on desktop; on mobile we always show it (touch
            devices have no hover state). */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShareOpen(true);
              }}
              className="mr-1 shrink-0 rounded p-1 text-muted-foreground/70 opacity-100 hover:bg-secondary hover:text-foreground md:opacity-0 md:group-hover/row:opacity-100 md:focus:opacity-100"
              aria-label={isOwner ? 'Manage access' : 'Who has access'}
            >
              <Share2 className="size-3.5" strokeWidth={1.75} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {isOwner ? 'Manage access' : 'Who has access'}
          </TooltipContent>
        </Tooltip>
      </div>
      <ShareAccountDialog account={a} open={shareOpen} onOpenChange={setShareOpen} />
    </>
  );
}

// --- Search input: operator help popover + recent-searches dropdown -----

const RECENT_SEARCHES_KEY = 'wharf:recent-searches';
const RECENT_SEARCHES_MAX = 6;

function loadRecentSearches(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string').slice(0, RECENT_SEARCHES_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecentSearch(q: string): void {
  if (!q) return;
  const cur = loadRecentSearches();
  const next = [q, ...cur.filter((s) => s !== q)].slice(0, RECENT_SEARCHES_MAX);
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    /* localStorage disabled — recent searches just won't persist */
  }
}

const SEARCH_OPERATORS: { op: string; example: string; desc: string }[] = [
  { op: 'from:', example: 'from:alice', desc: 'Sender contains' },
  { op: 'to:', example: 'to:team', desc: 'Recipient contains' },
  { op: 'subject:', example: 'subject:"q3 review"', desc: 'Subject contains' },
  { op: 'tag:', example: 'tag:urgent', desc: 'Has tag' },
  { op: 'is:', example: 'is:unread', desc: 'unread / starred / snoozed' },
  { op: 'before:', example: 'before:2026-01-01', desc: 'Before date (YYYY-MM-DD)' },
  { op: 'after:', example: 'after:2026-01-01', desc: 'After date' },
];

function SearchBar({
  value,
  onChange,
  inputRef,
  truncatedHint,
}: {
  value: string;
  onChange: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  truncatedHint?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>(() => loadRecentSearches());

  // Commit the current query to recent-searches when the input blurs while
  // showing a non-empty value. Avoids storing every keystroke.
  function commitRecent() {
    if (value.trim()) {
      pushRecentSearch(value.trim());
      setRecent(loadRecentSearches());
    }
  }

  return (
    <div className="border-b px-4 py-2">
      <div className="relative">
        <Search
          className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          strokeWidth={2}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search… (try from:alice)"
          className="w-full rounded-md border bg-card pl-7 pr-14 py-1.5 text-xs outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-ring"
          onFocus={() => setFocused(true)}
          onBlur={() => {
            commitRecent();
            // Defer so a click on a recent-search row registers before unmount.
            setTimeout(() => setFocused(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onChange('');
              inputRef.current?.blur();
            } else if (e.key === 'Enter') {
              commitRecent();
              inputRef.current?.blur();
            }
          }}
        />
        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3" strokeWidth={2.5} />
            </button>
          )}
          <DropdownMenu open={helpOpen} onOpenChange={setHelpOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded p-0.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                aria-label="Search operators"
                onMouseDown={(e) => e.preventDefault()}
              >
                ?
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 text-xs">
              <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Search operators
              </div>
              {SEARCH_OPERATORS.map((o) => (
                <button
                  key={o.op}
                  type="button"
                  className="flex w-full items-start gap-2 px-2 py-1.5 text-left hover:bg-accent/50"
                  onClick={() => {
                    setHelpOpen(false);
                    onChange((value ? value + ' ' : '') + o.op);
                    setTimeout(() => inputRef.current?.focus(), 0);
                  }}
                >
                  <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                    {o.op}
                  </code>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] text-foreground">{o.desc}</div>
                    <div className="truncate text-[10px] text-muted-foreground">{o.example}</div>
                  </div>
                </button>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Recent searches dropdown — visible while the input is focused +
          empty + history exists. */}
      {focused && !value && recent.length > 0 && (
        <div className="mt-1 rounded-md border bg-card shadow-sm">
          <div className="px-2 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Recent
          </div>
          <ul>
            {recent.map((r) => (
              <li key={r}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-xs hover:bg-accent/50"
                  // onMouseDown — blur happens on click otherwise the input
                  // loses focus before this fires.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(r);
                  }}
                >
                  <span className="truncate">{r}</span>
                  <button
                    type="button"
                    className="rounded p-0.5 text-muted-foreground/60 hover:text-destructive"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const next = recent.filter((x) => x !== r);
                      try {
                        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
                      } catch {/* ignore */}
                      setRecent(next);
                    }}
                    aria-label="Remove from history"
                  >
                    <X className="size-2.5" strokeWidth={2.5} />
                  </button>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {truncatedHint && (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          Searching most recent 500 threads — refine to find older.
        </p>
      )}
    </div>
  );
}

// --- Pull-to-refresh — touch-only wrapper for the thread list -----------
//
// We reach into the Radix ScrollArea viewport (via its data attribute) to
// read scrollTop. When the user is at the top + dragging down with their
// finger, a spinner banner grows above the list; release past the threshold
// fires onRefresh and stays anchored until it resolves.

function PullToRefresh({
  onRefresh,
  children,
}: {
  onRefresh: () => Promise<unknown> | unknown;
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const startY = useRef(0);
  const dragging = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const THRESHOLD = 64;
  const MAX_PULL = 120;

  function viewport(): HTMLElement | null {
    return rootRef.current?.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]') ?? null;
  }

  function onTouchStart(e: React.TouchEvent) {
    if (refreshing) return;
    const v = viewport();
    if (!v || v.scrollTop > 0) return;
    startY.current = e.touches[0]!.clientY;
    dragging.current = true;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (refreshing || !dragging.current) return;
    const v = viewport();
    if (!v) return;
    if (v.scrollTop > 0) {
      dragging.current = false;
      setPull(0);
      return;
    }
    const ddy = e.touches[0]!.clientY - startY.current;
    if (ddy > 0) {
      // Dampen past the threshold so it feels rubbery instead of unbounded.
      setPull(Math.min(MAX_PULL, ddy * 0.55));
    } else if (pull > 0) {
      setPull(0);
    }
  }

  function onTouchEnd() {
    if (refreshing || !dragging.current) {
      dragging.current = false;
      return;
    }
    dragging.current = false;
    if (pull > THRESHOLD) {
      setRefreshing(true);
      setPull(THRESHOLD);
      Promise.resolve(onRefresh()).finally(() => {
        // Brief beat so the spinner is visible even on a sub-100ms refetch.
        setTimeout(() => {
          setRefreshing(false);
          setPull(0);
        }, 350);
      });
    } else {
      setPull(0);
    }
  }

  return (
    <div
      ref={rootRef}
      className="relative flex min-h-0 flex-1 flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={() => {
        dragging.current = false;
        setPull(0);
      }}
    >
      <div
        className="flex shrink-0 items-end justify-center overflow-hidden"
        style={{
          height: pull,
          transition: dragging.current && !refreshing ? 'none' : 'height 0.22s ease-out',
        }}
        aria-hidden
      >
        {(pull > 0 || refreshing) && (
          <RefreshCw
            className={cn('mb-2 size-4 text-muted-foreground', refreshing && 'animate-spin')}
            strokeWidth={2}
            style={
              refreshing
                ? undefined
                : {
                    transform: `rotate(${(pull / THRESHOLD) * 270}deg)`,
                    opacity: Math.min(1, pull / 28),
                    transition: 'opacity 80ms linear',
                  }
            }
          />
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

// --- Skeleton rows shown while the thread list is first loading ----------

// --- Sender display-name + avatar helpers --------------------------------

/** Parse 'Name <addr@host>' style headers into a name + email pair.
 *  Falls back gracefully on bare addresses and empty strings. */
/** List-row display name: "Sandra from Flex Your Mindset", falling back to the
 *  address (or its local part) when the From header carries no name. */
function senderDisplay(from: string | null | undefined): string {
  const { name, email } = parseSender(from);
  return name || email || '(unknown sender)';
}

/** email → display name for everyone who has written on a thread. */
function namesOnThread(messages: ThreadMessage[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of messages) {
    const { name, email } = parseSender(m.from);
    if (email && name) out[email.toLowerCase()] = name;
  }
  return out;
}

function parseSender(raw: string | null | undefined): { name: string; email: string } {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { name: '', email: '' };
  const m = trimmed.match(/^(.*?)<([^>]+)>\s*$/);
  if (m) {
    const name = m[1]!.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, '');
    const email = m[2]!.trim();
    return { name, email };
  }
  // Bare 'name@host' — derive a display name from the local part.
  if (/^[^@\s]+@[^@\s]+$/.test(trimmed)) {
    const local = trimmed.split('@')[0] ?? '';
    return { name: local.replace(/[._-]+/g, ' '), email: trimmed };
  }
  return { name: trimmed, email: '' };
}

function senderInitials(name: string, email: string): string {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Deterministic colour from a string — picks one of 12 swatches keyed
 *  to a hash of the input so the same sender always lands on the same hue. */
const AVATAR_BG = [
  'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  'bg-pink-500/15 text-pink-700 dark:text-pink-300',
  'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
  'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
  'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  'bg-teal-500/15 text-teal-700 dark:text-teal-300',
  'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'bg-amber-500/15 text-amber-700 dark:text-amber-300',
];

function senderColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_BG[Math.abs(h) % AVATAR_BG.length]!;
}

// Freemail domains never have a meaningful company favicon — go straight to
// initials for those instead of burning a request per row.
const FREEMAIL = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'mail.com',
]);

// Session-scoped memo of domains the proxy returned 204 for, so list
// re-renders don't retry known-miss domains.
const avatarMisses = new Set<string>();

function SenderAvatar({
  from,
  size = 'md',
}: {
  from: string | null;
  size?: 'sm' | 'md';
}) {
  const { name, email } = parseSender(from);
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  const tryFavicon = !!domain && !FREEMAIL.has(domain) && !avatarMisses.has(domain);
  const [imgFailed, setImgFailed] = useState(false);
  const initials = senderInitials(name, email);
  const color = senderColor((email || name || '?').toLowerCase());
  const sizeCls = size === 'sm' ? 'size-6 text-[10px]' : 'size-8 text-xs';

  if (tryFavicon && !imgFailed) {
    return (
      <img
        src={`/api/avatar?domain=${encodeURIComponent(domain)}`}
        alt=""
        aria-hidden
        width={size === 'sm' ? 24 : 32}
        height={size === 'sm' ? 24 : 32}
        loading="lazy"
        className={cn('shrink-0 rounded-full border bg-white object-contain p-0.5', sizeCls)}
        title={name || email || undefined}
        onError={() => {
          avatarMisses.add(domain);
          setImgFailed(true);
        }}
        onLoad={(e) => {
          // The proxy answers 204 when there's no favicon — the img fires
          // load with zero natural size; treat as a miss.
          const img = e.currentTarget;
          if (!img.naturalWidth) {
            avatarMisses.add(domain);
            setImgFailed(true);
          }
        }}
      />
    );
  }

  return (
    <div
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-medium tabular-nums',
        sizeCls,
        color,
      )}
      title={name || email || undefined}
    >
      {initials}
    </div>
  );
}

// --- Empty-state component for the thread list ---------------------------

function EmptyState({
  folder,
  isSearching,
  q,
}: {
  folder: Folder;
  isSearching: boolean;
  q: string;
}) {
  if (isSearching) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
        <Search className="size-10 text-muted-foreground/40" strokeWidth={1.5} />
        <p className="mt-4 text-sm font-medium">No matches for &ldquo;{q}&rdquo;</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Try a different keyword, or click <span className="font-medium">?</span> for search operators.
        </p>
      </div>
    );
  }
  const copy: Record<Folder, { title: string; body: string; Icon: typeof InboxIcon }> = {
    all: {
      title: 'Nothing here yet',
      body: 'Sync will populate this in a moment.',
      Icon: Archive,
    },
    inbox: {
      title: "You're all caught up",
      body: 'No new mail right now. Time for a coffee.',
      Icon: InboxIcon,
    },
    assigned: {
      title: 'Nothing assigned to you',
      body: 'Conversations assigned to you will appear here.',
      Icon: UserCheck,
    },
    done: {
      title: 'Nothing marked done',
      body: 'Conversations you mark done will collect here.',
      Icon: CheckCircle2,
    },
    sent: {
      title: 'No sent mail',
      body: 'Compose a new message to start a thread.',
      Icon: Send,
    },
    drafts: {
      title: 'No drafts',
      body: 'Anything you start writing will show up here automatically.',
      Icon: FileText,
    },
    starred: {
      title: 'Nothing starred',
      body: 'Star a thread to keep it close at hand.',
      Icon: Star,
    },
    snoozed: {
      title: 'No snoozed threads',
      body: 'Threads you snooze for later will surface here when their time comes.',
      Icon: Clock,
    },
    spam: {
      title: 'No spam',
      body: 'Junk that Gmail flagged will collect here.',
      Icon: ShieldAlert,
    },
    trash: {
      title: 'Trash is empty',
      body: 'Deleted threads land here and stay for 30 days.',
      Icon: Trash,
    },
  };
  // Fall back to a generic empty state if a folder ever lacks copy, so a new
  // folder can never crash the whole view.
  const c = copy[folder] ?? copy.all;
  const Icon = c.Icon;
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="size-7" strokeWidth={1.5} />
      </div>
      <p className="mt-4 text-sm font-medium">{c.title}</p>
      <p className="mt-1 max-w-xs text-xs text-muted-foreground">{c.body}</p>
    </div>
  );
}

function ThreadRowSkeletons({ count = 8 }: { count?: number }) {
  // Vary widths so the placeholder doesn't read as a grid.
  const widths = ['w-3/5', 'w-2/3', 'w-4/5', 'w-1/2', 'w-3/4', 'w-2/5', 'w-7/12', 'w-5/6'];
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex w-full items-start gap-2 px-4 py-3">
          <div className="mt-0.5 size-4 shrink-0 rounded-sm bg-muted animate-pulse" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className={cn('h-3 rounded bg-muted animate-pulse', widths[i % widths.length])} />
              <div className="h-2.5 w-8 rounded bg-muted animate-pulse" />
            </div>
            <div className="h-2.5 w-11/12 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-2/3 rounded bg-muted/60 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Push toggle — sidebar footer button --------------------------------

function PushToggle() {
  const [state, setState] = useState(() => readPushState());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void currentSubscription().then((sub) => {
      if (!cancelled) setSubscribed(!!sub);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state.supported) return null;

  // iOS Safari (not in standalone): Web Push needs Add-to-Home-Screen first.
  if (state.needsInstall) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
        Tap <span className="font-medium text-foreground">Share → Add to Home Screen</span> to install Wharf Mail and enable notifications.
      </div>
    );
  }

  if (state.permission === 'denied') {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 p-2 text-[11px] text-muted-foreground">
        Notifications are blocked. Enable them in your browser settings.
      </div>
    );
  }

  async function toggle() {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
      } else {
        const ok = await enablePush();
        setSubscribed(ok);
        setState(readPushState());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-full justify-start gap-2"
      onClick={toggle}
      disabled={busy}
    >
      {subscribed ? (
        <>
          <BellOff className="size-3.5" strokeWidth={2} />
          Notifications on
        </>
      ) : (
        <>
          <Bell className="size-3.5" strokeWidth={2} />
          Enable notifications
        </>
      )}
    </Button>
  );
}
